"use client";

import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Copy,
  Edit3,
  FileClock,
  History,
  ListChecks,
  LockKeyhole,
  MessageSquareText,
  Megaphone,
  PackagePlus,
  Percent,
  Phone,
  Plus,
  ReceiptText,
  RefreshCcw,
  Search,
  Send,
  ShieldCheck,
  Smartphone,
  Trash2,
  UserPlus,
  UserRound,
  UsersRound,
  WalletCards,
  X
} from "lucide-react";
import {
  costCategories,
  customerTypes,
  districts,
  messageTemplates
} from "./parityData";
import type {
  CostEntry,
  Customer,
  CustomerAppCredential,
  CustomerExperienceLevel,
  LedgerEntry,
  Opportunity,
  PackageRecord,
  TrainerException
} from "./types";
import { useStudio } from "./StudioContext";
import { StudioApiError, studioApi, studioErrorCopy } from "./lib/api";
import { packageIdentity } from "./lib/packageIdentity";
import { normalizeTurkishMobile, smsPhoneState } from "./lib/phone";
import {
  announcementErrorCopy,
  normalizeStudioAnnouncement,
  resolveStudioAnnouncementAttemptKey,
  studioAnnouncementReviewRows,
  studioAnnouncementTargets,
  type StudioAnnouncementResult,
  type StudioAnnouncementTarget
} from "./lib/studioAnnouncement";
import { addIsoDays, istanbulToday, monthBounds, shiftIsoMonth } from "./lib/date";
import { RegistrationFormPanel } from "./RegistrationFormPanel";
import { CustomerHistorySummary } from "./CustomerHistorySummary";
import { CustomerHistoryToolbar } from "./CustomerHistoryToolbar";

const money = (amount: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0
  }).format(amount);

const preciseMoney = (amount: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);

const customerExperienceLevels: ReadonlyArray<{
  code: CustomerExperienceLevel;
  label: string;
}> = [
  { code: "BEGINNER", label: "Başlangıç" },
  { code: "BEGINNER_INTERMEDIATE", label: "Başlangıç-Orta" },
  { code: "INTERMEDIATE", label: "Orta" },
  { code: "INTERMEDIATE_ADVANCED", label: "Orta-İleri" },
  { code: "ADVANCED", label: "İleri" }
];

const customerExperienceLevelLabel = (level?: CustomerExperienceLevel) =>
  customerExperienceLevels.find((item) => item.code === (level ?? "INTERMEDIATE"))?.label ?? "Orta";

const percentText = (amount: number) =>
  new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);

const shortDate = (date?: string) =>
  date
    ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "short", year: "numeric" }).format(
        new Date(`${date}T12:00:00`)
      )
    : "—";

const shortDateTime = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("tr-TR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }).format(new Date(value))
    : "—";

const today = istanbulToday;

const fullHourOptions = Array.from(
  { length: 16 },
  (_, index) => `${String(index + 7).padStart(2, "0")}:00`
);

const monthRange = (offset = 0) => {
  const key = shiftIsoMonth(istanbulToday(), offset).slice(0, 7);
  const bounds = monthBounds(key);
  return { from: bounds.start, to: bounds.end, key };
};

const monthCaption = (key: string, style: "short" | "long" = "short") =>
  new Intl.DateTimeFormat("tr-TR", { month: style, year: style === "long" ? "numeric" : undefined })
    .format(new Date(`${key}-15T12:00:00`));

const rollingYearStart = () => {
  return shiftIsoMonth(istanbulToday(), -12);
};

const ledgerTypeLabel = (description: LedgerEntry["description"]) =>
  description === "Record"
    ? "Kayıt"
    : description === "Session"
      ? "Seans"
      : description === "Workshop"
        ? "Atölye"
        : description === "Payment"
          ? "Ödeme"
          : "Düzeltme";

const ledgerQuantityLabel = (entry: LedgerEntry) => {
  if (entry.quantity == null) return "—";
  if (entry.description === "Workshop" && entry.quantity > 0) return `+${entry.quantity}`;
  return String(entry.quantity);
};

function Heading({
  eyebrow,
  title,
  description,
  action
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

function PriceCatalog() {
  const { packagePlans, updatePackagePlanPrices } = useStudio();
  const plans = packagePlans.filter((plan) => plan.isActive);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draftPrices, setDraftPrices] = useState<Record<string, string>>({});
  const [bulkPercent, setBulkPercent] = useState("");
  const [bulkInputFocused, setBulkInputFocused] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkAdjustments, setBulkAdjustments] = useState<Record<string, {
    requestedPercent: number;
    calculatedPrice: number;
    roundedPrice: number;
  }>>({});
  const [financePin, setFinancePin] = useState("");
  const [saveError, setSaveError] = useState("");

  const priceState = useMemo(() => {
    const changed: Array<{ id: string; version: string; defaultPrice: number }> = [];
    const invalidIds = new Set<string>();
    const values: Record<string, number> = {};

    plans.forEach((plan) => {
      const raw = draftPrices[plan.id] ?? String(plan.defaultPrice ?? "");
      const normalized = raw.trim().replace(",", ".");
      if (!normalized) {
        if (plan.defaultPrice != null) invalidIds.add(plan.id);
        return;
      }
      const nextPrice = Number(normalized);
      if (!Number.isFinite(nextPrice) || nextPrice <= 0 || nextPrice > 99_999_999.99) {
        invalidIds.add(plan.id);
        return;
      }
      values[plan.id] = nextPrice;
      if (plan.defaultPrice == null || Math.abs(nextPrice - plan.defaultPrice) >= 0.005) {
        changed.push({ id: plan.id, version: plan.version, defaultPrice: nextPrice });
      }
    });

    return { changed, invalidIds, values };
  }, [draftPrices, plans]);

  const bulkStats = useMemo(() => {
    const adjustments = Object.values(bulkAdjustments);
    return {
      adjustedCount: adjustments.length,
      roundedCount: adjustments.filter((item) => Math.abs(item.calculatedPrice - item.roundedPrice) >= 0.005).length
    };
  }, [bulkAdjustments]);

  function openEditor(focusBulkInput = false) {
    setDraftPrices(
      Object.fromEntries(plans.map((plan) => [plan.id, plan.defaultPrice == null ? "" : String(plan.defaultPrice)]))
    );
    setBulkPercent("");
    setBulkInputFocused(focusBulkInput);
    setBulkError("");
    setBulkAdjustments({});
    setFinancePin("");
    setSaveError("");
    setEditorOpen(true);
  }

  function closeEditor() {
    setFinancePin("");
    setSaveError("");
    setBulkError("");
    setBulkAdjustments({});
    setEditorOpen(false);
  }

  function applyBulkIncrease() {
    const requestedPercent = Number(bulkPercent.trim().replace(",", "."));
    if (!Number.isFinite(requestedPercent) || requestedPercent <= 0 || requestedPercent > 1000) {
      setBulkError("Sıfırdan büyük geçerli bir zam oranı girin.");
      return;
    }

    const nextDrafts: Record<string, string> = {};
    const nextAdjustments: typeof bulkAdjustments = {};
    plans.forEach((plan) => {
      if (plan.defaultPrice == null || plan.defaultPrice <= 0) return;
      const calculatedPrice = plan.defaultPrice * (1 + requestedPercent / 100);
      const roundedPrice = Math.round(calculatedPrice / 500) * 500;
      nextDrafts[plan.id] = String(roundedPrice);
      nextAdjustments[plan.id] = { requestedPercent, calculatedPrice, roundedPrice };
    });

    setDraftPrices((current) => ({ ...current, ...nextDrafts }));
    setBulkAdjustments(nextAdjustments);
    setBulkError("");
    setSaveError("");
  }

  async function savePrices(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!priceState.changed.length || priceState.invalidIds.size || !financePin.trim()) return;
    setSaveError("");
    try {
      await updatePackagePlanPrices(priceState.changed, financePin);
      closeEditor();
    } catch (error) {
      setSaveError(studioErrorCopy(error, "Fiyatlar kaydedilemedi. Güncel listeyi yükleyip tekrar deneyin."));
    }
  }

  return (
    <>
      <div className="price-workspace">
        <div className="price-title">
          <div><span className="eyebrow">SQL fiyat kataloğu · canlı</span><h2>The Heal Hub fiyat listesi</h2></div>
          <div className="price-title-actions">
            <span>Yeni paketlerde otomatik uygulanır</span>
            <div className="price-title-buttons">
              <button className="button price-bulk-button" onClick={() => openEditor(true)} disabled={!plans.length}><Percent size={16} /> Genel düzenleme</button>
              <button className="button price-edit-button" onClick={() => openEditor(false)} disabled={!plans.length}><Edit3 size={16} /> Fiyatları düzenle</button>
            </div>
          </div>
        </div>
        <div className="data-panel catalog-price-table">
          <div className="table-head catalog-price-grid"><span>Hizmet</span><span>Seans</span><span>Liste fiyatı</span><span>Geçerlilik</span><span>İptal hakkı</span></div>
          {plans.map((plan) => <div className="table-row catalog-price-grid" key={plan.code}><strong>{plan.serviceTypeName}</strong><span>{plan.sessionCount}</span><strong>{plan.defaultPrice == null ? "Tanımsız" : money(Number(plan.defaultPrice))}<small>KDV hariç</small></strong><span>{plan.validityDays} gün</span><span>{plan.cancellationAllowance}</span></div>)}
          {!plans.length && <div className="empty-state compact"><ReceiptText size={24} /><strong>Fiyat kataloğu hazırlanıyor</strong><span>SQL fiyat kataloğu scripti çalıştırıldığında fiyatlar burada görünecek.</span></div>}
        </div>
      </div>
      {editorOpen && (
        <Modal title="Fiyat kataloğunu güncelle" eyebrow="Yeni satış fiyatları" onClose={closeEditor} wide>
          <form className="catalog-price-editor" onSubmit={savePrices}>
            <div className="catalog-editor-note">
              <CircleDollarSign size={19} />
              <span><strong>Değişiklik yalnızca yeni paket satışlarına uygulanır.</strong><small>Daha önce satılan paketlerin fiyatı ve kalan borcu değişmez.</small></span>
            </div>
            <section className="catalog-bulk-tool" aria-label="Toplu zam hesaplama">
              <div className="catalog-bulk-copy">
                <span className="catalog-bulk-icon"><Percent size={18} /></span>
                <span><strong>Tüm fiyatlara zam uygula</strong><small>Hesaplanan tutarlar en yakın 500 TL seviyesine yuvarlanır.</small></span>
              </div>
              <div className="catalog-bulk-control">
                <label>
                  <span>Zam oranı</span>
                  <span className="catalog-percent-input">
                    <input
                      inputMode="decimal"
                      autoFocus={bulkInputFocused}
                      value={bulkPercent}
                      onFocus={() => setBulkInputFocused(false)}
                      onChange={(event) => {
                        setBulkPercent(event.target.value.replace(/[^\d,.]/g, ""));
                        setBulkError("");
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter") return;
                        event.preventDefault();
                        applyBulkIncrease();
                      }}
                      aria-label="Genel zam oranı"
                      placeholder="10"
                    />
                    <i>%</i>
                  </span>
                </label>
                <button type="button" className="button primary catalog-bulk-apply" onClick={applyBulkIncrease}>Fiyatlara uygula <ArrowRight size={16} /></button>
              </div>
              {bulkError && <div className="catalog-bulk-error">{bulkError}</div>}
              {!!bulkStats.adjustedCount && (
                <div className="catalog-bulk-result">
                  <strong>%{percentText(Object.values(bulkAdjustments)[0]?.requestedPercent ?? 0)} zam taslağı hazır</strong>
                  <span>{bulkStats.adjustedCount} fiyat hesaplandı · {bulkStats.roundedCount} fiyat 500 TL adımına yuvarlandı</span>
                </div>
              )}
            </section>
            <div className="catalog-editor-table">
              <div className="catalog-editor-head"><span>Paket</span><span>Mevcut fiyat</span><span>Yeni fiyat</span><span>Değişim</span></div>
              {plans.map((plan) => {
                const changed = priceState.changed.some((item) => item.id === plan.id);
                const invalid = priceState.invalidIds.has(plan.id);
                const nextPrice = priceState.values[plan.id];
                const adjustment = bulkAdjustments[plan.id];
                const manuallyEdited = !!adjustment && nextPrice != null && Math.abs(nextPrice - adjustment.roundedPrice) >= 0.005;
                const actualPercent = plan.defaultPrice != null && nextPrice != null
                  ? ((nextPrice - plan.defaultPrice) / plan.defaultPrice) * 100
                  : null;
                return (
                  <label className={`catalog-editor-row ${changed ? "changed" : ""} ${invalid ? "invalid" : ""}`} key={plan.id}>
                    <span><strong>{plan.serviceTypeName}</strong><small>{plan.sessionCount} seans · {plan.validityDays} gün</small></span>
                    <span>{plan.defaultPrice == null ? "Tanımsız" : money(plan.defaultPrice)}</span>
                    <span className="catalog-price-input"><input inputMode="decimal" value={draftPrices[plan.id] ?? ""} onChange={(event) => setDraftPrices((current) => ({ ...current, [plan.id]: event.target.value.replace(/[^\d,.]/g, "") }))} aria-label={`${plan.name} yeni fiyatı`} /><i>₺</i></span>
                    <span className={`catalog-change-note ${changed ? "active" : ""}`}>
                      {changed && actualPercent != null ? (
                        <>
                          <strong>{actualPercent >= 0 ? "+" : "−"}%{percentText(Math.abs(actualPercent))}</strong>
                          <small>
                            {adjustment && !manuallyEdited
                              ? Math.abs(adjustment.calculatedPrice - adjustment.roundedPrice) >= 0.005
                                ? `${preciseMoney(adjustment.calculatedPrice)} → ${money(adjustment.roundedPrice)} yuvarlandı`
                                : "Yuvarlama gerekmedi"
                              : adjustment && manuallyEdited
                                ? "Yuvarlama sonrası elle düzenlendi"
                                : "Elle düzenlendi"}
                          </small>
                        </>
                      ) : plan.defaultPrice == null ? (
                        <small>Baz fiyat yok · elle girilebilir</small>
                      ) : (
                        <small>Değişiklik yok</small>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {saveError && <div className="form-error">{saveError}</div>}
            {!!priceState.invalidIds.size && <div className="form-error">Fiyat boş bırakılamaz; sıfırdan büyük geçerli bir tutar girin.</div>}
            <label className="field catalog-finance-pin"><span>Kasa yetki kodu</span><input type="password" inputMode="numeric" autoComplete="off" value={financePin} onChange={(event) => setFinancePin(event.target.value)} placeholder="Fiyat değişikliğini onaylamak için girin" /></label>
            <div className="catalog-editor-summary">
              <span><strong>{priceState.changed.length}</strong> fiyat değişecek</span>
              <small>Kaydetme işlemi tek transaction içinde tamamlanır ve işlem günlüğüne yazılır.</small>
            </div>
            <div className="modal-actions">
              <button type="button" className="button secondary" onClick={closeEditor}>Vazgeç</button>
              <button type="submit" className="button primary" disabled={!priceState.changed.length || !!priceState.invalidIds.size || !financePin.trim()}><Check size={16} /> Değişiklikleri kaydet</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}

function Modal({
  title,
  eyebrow,
  onClose,
  children,
  wide = false
}: {
  title: string;
  eyebrow: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Kapat" />
      <div className={`modal-card ${wide ? "modal-wide" : ""}`} role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div>
          <button className="icon-button" onClick={onClose} aria-label={`${title} penceresini kapat`}><X size={20} /></button>
        </div>
        {children}
      </div>
    </>
  );
}

function CustomerForm({
  initial,
  onCancel,
  onSave
}: {
  initial?: Customer;
  onCancel: () => void;
  onSave: (customer: Customer) => void | Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get("name"));
    const rawPhone = String(data.get("phone") ?? "").trim();
    const normalizedPhone = rawPhone ? normalizeTurkishMobile(rawPhone) : "";
    if (rawPhone && !normalizedPhone) {
      setError("Telefon numarası 05xx xxx xx xx biçiminde olmalı veya boş bırakılmalı.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        id: initial?.id ?? `C${String(Date.now()).slice(-5)}`,
        name,
        initials: name.split(" ").map((part) => part[0]).join("").slice(0, 2).toLocaleUpperCase("tr-TR"),
        phone: normalizedPhone || "",
        email: String(data.get("email")),
        packageName: initial?.packageName ?? "Paket tanımlanmadı",
        remainingSessions: initial?.remainingSessions ?? 0,
        nextAppointment: initial?.nextAppointment ?? "Planlanmadı",
        status: data.get("active") === "on" ? "active" : "inactive",
        gender: String(data.get("gender")) as "Woman" | "Man",
        typeOf: String(data.get("typeOf")),
        birthDate: String(data.get("birthDate")),
        district: String(data.get("district")),
        notes: String(data.get("notes")),
        history: String(data.get("history")),
        registrationDate: initial?.registrationDate ?? today(),
        dontSendSms: !normalizedPhone || data.get("allowSms") !== "on",
        requiresExclusiveSlot: data.get("requiresExclusiveSlot") === "on",
        avoidsMaleCoAttendee: data.get("avoidsMaleCoAttendee") === "on",
        experienceLevel: String(data.get("experienceLevel") ?? "INTERMEDIATE") as CustomerExperienceLevel,
        registrationFormSigned: initial?.registrationFormSigned ?? false
      });
    } catch (saveError) {
      setError(studioErrorCopy(saveError, "Müşteri kaydedilemedi. Bilgileri kontrol edip yeniden deneyin."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <div className="form-row">
        <label className="field"><span>Müşteri no</span><input value={initial?.id ?? "Otomatik oluşturulur"} disabled /></label>
        <label className="field"><span>Ad soyad</span><input name="name" defaultValue={initial?.name} required /></label>
      </div>
      <div className="form-row">
        <label className="field"><span>Telefon <i>isteğe bağlı</i></span><input name="phone" inputMode="tel" autoComplete="tel" placeholder="05xx xxx xx xx" defaultValue={initial?.phone} /></label>
        <label className="field"><span>E-posta</span><input name="email" type="email" defaultValue={initial?.email} /></label>
      </div>
      <div className="form-row">
        <label className="field"><span>Cinsiyet</span><select name="gender" defaultValue={initial?.gender ?? "Woman"}><option value="Woman">Kadın</option><option value="Man">Erkek</option></select></label>
        <label className="field"><span>Doğum tarihi</span><input name="birthDate" type="date" defaultValue={initial?.birthDate} /></label>
      </div>
      <div className="form-row">
        <label className="field"><span>Müşteri tipi</span><select name="typeOf" defaultValue={initial?.typeOf ?? "Pilates"}>{customerTypes.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="field"><span>İlçe</span><select name="district" defaultValue={initial?.district ?? "Kadıköy"}>{districts.map((item) => <option key={item}>{item}</option>)}</select></label>
      </div>
      <label className="field full">
        <span>Seviye</span>
        <select name="experienceLevel" defaultValue={initial?.experienceLevel ?? "INTERMEDIATE"}>
          {customerExperienceLevels.map((level) => (
            <option key={level.code} value={level.code}>{level.label}</option>
          ))}
        </select>
      </label>
      <label className="field full"><span>Notlar</span><textarea name="notes" rows={2} defaultValue={initial?.notes} /></label>
      <label className="field full"><span>Geçmiş</span><textarea name="history" rows={2} defaultValue={initial?.history} /></label>
      <fieldset className="customer-preference-fieldset">
        <legend>Seans eşleşme tercihleri</legend>
        <p>Aynı saatte başka danışan planlanırken ekibin dikkate alacağı tercihler.</p>
        <div className="customer-preference-grid">
          <label className="customer-preference-choice">
            <input
              name="requiresExclusiveSlot"
              type="checkbox"
              defaultChecked={initial?.requiresExclusiveSlot ?? false}
            />
            <span>
              <strong>Danışan yanına birini istemiyor</strong>
              <small>Bu saat yalnızca bu danışan için planlanır.</small>
            </span>
          </label>
          <label className="customer-preference-choice">
            <input
              name="avoidsMaleCoAttendee"
              type="checkbox"
              defaultChecked={initial?.avoidsMaleCoAttendee ?? false}
            />
            <span>
              <strong>Danışan erkek danışan istemiyor</strong>
              <small>Aynı saatte erkek danışanla eşleştirilmez.</small>
            </span>
          </label>
        </div>
      </fieldset>
      <label className="check-row"><input name="active" type="checkbox" defaultChecked={initial?.status !== "inactive"} /><span>Aktif müşteri</span></label>
      <label className="check-row"><input name="allowSms" type="checkbox" defaultChecked={initial ? !initial.dontSendSms && Boolean(initial.phone) : true} /><span>SMS gönderimine izin veriyor</span></label>
      <p className="field-help">Telefon boş bırakılırsa SMS izni otomatik olarak kapatılır.</p>
      {error && <div className="form-error">{error}</div>}
      <div className="modal-actions">
        <button type="button" className="button ghost" onClick={onCancel}>Vazgeç</button>
        <button className="button primary" disabled={saving}>{saving ? "Kaydediliyor…" : "Bilgileri kaydet"}</button>
      </div>
    </form>
  );
}

export function PackageCreationForm({
  customer,
  customerRecords,
  onSave,
  onCancel,
  compact = false
}: {
  customer: Customer;
  customerRecords: PackageRecord[];
  onSave: (record: PackageRecord) => Promise<void>;
  onCancel?: () => void;
  compact?: boolean;
}) {
  const { packagePlans, paymentMethods, packageRules } = useStudio();
  const pricedPlans = packagePlans.filter((plan) => plan.isActive && plan.defaultPrice != null);
  const services = Array.from(
    new Map(
      pricedPlans.map((plan) => [
        plan.serviceTypeCode,
        { code: plan.serviceTypeCode, name: plan.serviceTypeName }
      ])
    ).values()
  );
  const initialService = services.find((service) => service.code === "INDIVIDUAL_PILATES")?.code ?? services[0]?.code ?? "";
  const [serviceCode, setServiceCode] = useState(initialService);
  const [planCode, setPlanCode] = useState("");
  const [specialPricing, setSpecialPricing] = useState(false);
  const [customQuantity, setCustomQuantity] = useState(8);
  const [customPrice, setCustomPrice] = useState(0);
  const [discountType, setDiscountType] = useState<"NONE" | "LOYALTY" | "STAFF">("NONE");
  const [expectedPaymentMethodCode, setExpectedPaymentMethodCode] = useState("");
  const [vatApplied, setVatApplied] = useState(false);
  const [purchasedOn, setPurchasedOn] = useState(today());
  const [validityDays, setValidityDays] = useState(packageRules.eight.validityDays);
  const [customExpiryDate, setCustomExpiryDate] = useState(
    addIsoDays(today(), packageRules.eight.validityDays)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const servicePlans = pricedPlans.filter((plan) => plan.serviceTypeCode === serviceCode);
  const selectedPlan = servicePlans.find((plan) => plan.code === planCode) ?? servicePlans[0];
  const selectedService = services.find((service) => service.code === serviceCode);
  const pilatesRecordCount = customerRecords.filter(
    (record) =>
      record.isOperational !== false &&
      (record.serviceTypeCode?.includes("PILATES") || record.detail.toLocaleLowerCase("tr-TR").includes("pilates"))
  ).length;
  const loyaltyPercent = pilatesRecordCount >= 6 ? 10 : pilatesRecordCount >= 3 ? 5 : 0;
  const discountPercent =
    discountType === "STAFF" ? 10 : discountType === "LOYALTY" ? loyaltyPercent : 0;
  const listAmount = specialPricing ? customPrice : Number(selectedPlan?.defaultPrice ?? 0);
  const quantity = specialPricing ? customQuantity : Number(selectedPlan?.sessionCount ?? 0);
  const serviceRuleKey = `${selectedService?.code ?? ""} ${selectedService?.name ?? ""}`.toLocaleUpperCase("tr-TR");
  const isManualOrSchroth = serviceRuleKey.includes("MANUEL") || serviceRuleKey.includes("SCHRO");
  const durationRule =
    isManualOrSchroth && quantity === 5
      ? packageRules.manualSchrothFive
      : isManualOrSchroth && quantity === 10
        ? packageRules.manualSchrothTen
        : quantity === 8
          ? packageRules.eight
          : quantity === 16
            ? packageRules.sixteen
            : {
                validityDays: Number(selectedPlan?.validityDays ?? packageRules.eight.validityDays),
                alternativeValidityDays: Number(selectedPlan?.validityDays ?? packageRules.eight.validityDays)
              };
  const durationOptions = Array.from(
    new Set([durationRule.validityDays, durationRule.alternativeValidityDays])
  );
  const expiryDate = specialPricing ? customExpiryDate : addIsoDays(purchasedOn, validityDays);
  const netAmount = Math.max(0, Math.round(listAmount * (1 - discountPercent / 100)));
  const vatRate = 20;
  const vatAmount = vatApplied ? Math.round(netAmount * vatRate / 100) : 0;
  const finalAmount = netAmount + vatAmount;
  const selectablePaymentMethods = paymentMethods.filter((method) => method.code !== "UNKNOWN");
  const expectedPaymentMethod = selectablePaymentMethods.find(
    (method) => method.code === expectedPaymentMethodCode
  );

  useEffect(() => {
    if (!services.some((service) => service.code === serviceCode)) {
      setServiceCode(initialService);
    }
  }, [initialService, serviceCode, services]);

  useEffect(() => {
    const firstPlan = pricedPlans.find((plan) => plan.serviceTypeCode === serviceCode);
    setPlanCode(firstPlan?.code ?? "");
  }, [serviceCode, packagePlans]);

  useEffect(() => {
    setValidityDays(durationRule.validityDays);
  }, [serviceCode, selectedPlan?.code, specialPricing, customQuantity, durationRule.validityDays]);

  useEffect(() => {
    if (customExpiryDate < purchasedOn) {
      setCustomExpiryDate(addIsoDays(purchasedOn, packageRules.eight.validityDays));
    }
  }, [customExpiryDate, purchasedOn, packageRules.eight.validityDays]);

  useEffect(() => {
    if (selectablePaymentMethods.some((method) => method.code === expectedPaymentMethodCode)) return;
    const cash = selectablePaymentMethods.find((method) => method.code === "CASH");
    setExpectedPaymentMethodCode(cash?.code ?? selectablePaymentMethods[0]?.code ?? "");
  }, [expectedPaymentMethodCode, paymentMethods]);

  function selectPaymentMethod(code: string) {
    setExpectedPaymentMethodCode(code);
    setVatApplied(code === "CREDIT_CARD" || code === "BANK_TRANSFER");
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedService || quantity < 1 || listAmount <= 0 || expiryDate < purchasedOn || (!specialPricing && !selectedPlan)) {
      setError("Hizmet, seans adedi veya fiyat bilgisi eksik.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        id: `REC-${String(Date.now()).slice(-6)}`,
        customerId: customer.id,
        customer: customer.name,
        detail: selectedService.name,
        quantity,
        price: finalAmount,
        listAmount,
        registrationDate: purchasedOn,
        expiryDate,
        packagePlanCode: specialPricing ? undefined : selectedPlan?.code,
        serviceTypeCode: selectedService.code,
        pricingMode: specialPricing ? "CUSTOM" : "CATALOG",
        discountType,
        discountPercent,
        expectedPaymentMethodCode,
        expectedPaymentMethod: expectedPaymentMethod?.name,
        vatApplied,
        vatRate,
        vatAmount,
        netAmount,
        cancellationAllowance: specialPricing ? undefined : selectedPlan?.cancellationAllowance
      });
    } catch (saveError) {
      setError(studioErrorCopy(saveError, "Paket oluşturulamadı. Bilgileri kontrol edin."));
      return;
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className={compact ? "package-builder compact" : "package-builder"} onSubmit={submit}>
      {!pricedPlans.length && <div className="form-error">SQL fiyat kataloğu henüz hazırlanmadı. Fiyat kataloğu scriptini çalıştırın.</div>}
      <label className="field full">
        <span>Seans türü</span>
        <select value={serviceCode} onChange={(event) => setServiceCode(event.target.value)} required>
          {services.map((service) => <option key={service.code} value={service.code}>{service.name}</option>)}
        </select>
      </label>
      <label className="special-pricing-toggle">
        <input type="checkbox" checked={specialPricing} onChange={(event) => setSpecialPricing(event.target.checked)} />
        <span><strong>Özel fiyatlandırma</strong><small>Seans adedini ve liste fiyatını elle belirle.</small></span>
      </label>
      {specialPricing ? (
        <div className="form-row">
          <label className="field"><span>Seans adedi</span><input type="number" min="1" max="500" value={customQuantity} onChange={(event) => setCustomQuantity(Number(event.target.value))} required /></label>
          <label className="field"><span>Liste fiyatı</span><input type="number" min="1" step="1" value={customPrice || ""} onChange={(event) => setCustomPrice(Number(event.target.value))} required /></label>
        </div>
      ) : (
        <label className="field full">
          <span>Paket / seans adedi</span>
          <select value={selectedPlan?.code ?? ""} onChange={(event) => setPlanCode(event.target.value)} required>
            {servicePlans.map((plan) => <option key={plan.code} value={plan.code}>{plan.sessionCount} seans · {money(Number(plan.defaultPrice))}</option>)}
          </select>
        </label>
      )}
      <div className="discount-choice-grid">
        <label className={discountType === "LOYALTY" ? "selected" : ""}>
          <input type="radio" name="discount" checked={discountType === "LOYALTY"} disabled={!loyaltyPercent} onChange={() => setDiscountType("LOYALTY")} />
          <span><strong>Sadakat indirimi</strong><small>{loyaltyPercent ? `%${loyaltyPercent} uygulanır` : "Bu müşteri henüz uygun değil"}</small></span>
        </label>
        <label className={discountType === "STAFF" ? "selected" : ""}>
          <input type="radio" name="discount" checked={discountType === "STAFF"} onChange={() => setDiscountType("STAFF")} />
          <span><strong>Çalışan indirimi</strong><small>En fazla %10 uygulanır</small></span>
        </label>
        {discountType !== "NONE" && <button type="button" className="text-button" onClick={() => setDiscountType("NONE")}>İndirimi kaldır</button>}
      </div>
      <div className="package-validity-row">
        <label className="field">
          <span>Kayıt tarihi</span>
          <input type="date" value={purchasedOn} onChange={(event) => setPurchasedOn(event.target.value)} required />
        </label>
        {specialPricing ? (
          <label className="field">
            <span>Son kullanım tarihi</span>
            <input type="date" min={purchasedOn} value={customExpiryDate} onChange={(event) => setCustomExpiryDate(event.target.value)} required />
          </label>
        ) : (
          <label className="field">
            <span>Paket süresi</span>
            <select value={validityDays} onChange={(event) => setValidityDays(Number(event.target.value))} required>
              {durationOptions.map((days) => <option key={days} value={days}>{days} gün · {shortDate(addIsoDays(purchasedOn, days))}</option>)}
            </select>
          </label>
        )}
      </div>
      <div className="package-expiry-note">
        <CalendarClock size={15} />
        <span>Son kullanım: <strong>{shortDate(expiryDate)}</strong>{!specialPricing && ` · ${validityDays} gün seçildi`}</span>
      </div>
      <div className="package-payment-row">
        <label className="field">
          <span>Tahsilat tercihi</span>
          <select value={expectedPaymentMethodCode} onChange={(event) => selectPaymentMethod(event.target.value)} required>
            {selectablePaymentMethods.map((method) => <option key={method.code} value={method.code}>{method.name}</option>)}
          </select>
        </label>
        <label className={`vat-toggle ${vatApplied ? "selected" : ""}`}>
          <input type="checkbox" checked={vatApplied} onChange={(event) => setVatApplied(event.target.checked)} />
          <span><strong>%20 KDV dahil et</strong><small>Kredi kartı ve havalede otomatik açılır; elle değiştirilebilir.</small></span>
        </label>
      </div>
      <div className="package-price-summary">
        <div><span>Liste fiyatı</span><strong>{money(listAmount)}</strong></div>
        <div><span>İndirim</span><strong>{discountPercent ? `%${discountPercent}` : "Yok"}</strong></div>
        <div><span>İndirim sonrası</span><strong>{money(netAmount)}</strong></div>
        <div><span>KDV</span><strong>{vatApplied ? money(vatAmount) : "Eklenmedi"}</strong></div>
        <div className="total"><span>Genel toplam</span><strong>{money(finalAmount)}</strong></div>
      </div>
      <div className="availability-note"><Check size={16} /><span><strong>Tek işlemde kaydedilir:</strong> Paket, seans bakiyesi ve fiyat bilgisi.</span></div>
      {error && <div className="form-error">{error}</div>}
      <div className="modal-actions">
        {onCancel && <button type="button" className="button ghost" onClick={onCancel}>Vazgeç</button>}
        <button className="button primary" disabled={saving || !pricedPlans.length || finalAmount <= 0}>{saving ? "Paket oluşturuluyor…" : "Paketi oluştur"}</button>
      </div>
    </form>
  );
}

export function PackageRecordUpdateForm({
  record,
  paymentMethods,
  onCancel,
  onSave
}: {
  record: PackageRecord;
  paymentMethods: Array<{ code: string; name: string }>;
  onCancel: () => void;
  onSave: (record: PackageRecord) => void | Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const selectablePaymentMethods = paymentMethods.filter((method) => method.code !== "UNKNOWN");

  return (
    <form onSubmit={async (event) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const finalPrice = Number(data.get("price"));
      setSaving(true);
      setError("");
      try {
        await onSave({
          ...record,
          pricingMode: "FINAL_OVERRIDE",
          listAmount: finalPrice,
          netAmount: finalPrice,
          vatApplied: false,
          vatAmount: 0,
          discountType: "NONE",
          discountPercent: 0,
          price: finalPrice,
          quantity: Number(data.get("quantity")),
          expiryDate: String(data.get("expiryDate")),
          cancellationAllowance: Number(data.get("cancellationAllowance")),
          expectedPaymentMethodCode: String(data.get("expectedPaymentMethodCode"))
        });
      } catch (updateError) {
        setError(studioErrorCopy(updateError, "Paket güncellenemedi. Bilgileri kontrol edin."));
      } finally {
        setSaving(false);
      }
    }}>
      <div className="form-row"><label className="field"><span>Yeni son fiyat</span><input name="price" type="number" min="1" defaultValue={record.price} required /></label><label className="field"><span>Yeni seans adedi</span><input name="quantity" type="number" min="1" max="500" defaultValue={record.quantity} required /></label></div>
      <div className="form-row">
        <label className="field"><span>Son kullanım tarihi</span><input name="expiryDate" type="date" min={record.registrationDate} defaultValue={record.expiryDate} required /></label>
        <label className="field"><span>Toplam iptal hakkı</span><input name="cancellationAllowance" type="number" min={record.cancellationsUsed ?? 0} max="500" defaultValue={record.cancellationAllowance ?? 0} required /></label>
      </div>
      <label className="field full">
        <span>Beklenen ödeme yöntemi</span>
        <select name="expectedPaymentMethodCode" defaultValue={record.expectedPaymentMethodCode || selectablePaymentMethods[0]?.code} required>
          {selectablePaymentMethods.map((method) => <option value={method.code} key={method.code}>{method.name}</option>)}
        </select>
      </label>
      <div className="availability-note"><History size={18} /><span><strong>Girdiğin tutar doğrudan son fiyattır.</strong> Üzerine indirim veya KDV hesaplanmaz; bağlı seans ve ödemeler korunur.</span></div>
      {error && <div className="form-error">{error}</div>}
      <div className="modal-actions"><button type="button" className="button ghost" disabled={saving} onClick={onCancel}>Vazgeç</button><button className="button primary" disabled={saving || !selectablePaymentMethods.length}>{saving ? "Güncelleniyor…" : "Değişikliği kaydet"}</button></div>
    </form>
  );
}

function CustomerDetail({
  customer,
  records,
  onClose,
  onUpdate,
  onAddRecord,
  onUpdateRecord,
  onBurnRecord,
  onAddPayment,
  onRemovePayment,
  onNewAppointment,
  onToast,
  initialMobileCredential,
  payments,
  ledger
}: {
  customer: Customer;
  records: PackageRecord[];
  onClose: () => void;
  onUpdate: (customer: Customer) => void | Promise<void>;
  onAddRecord: (record: PackageRecord) => void | Promise<void>;
  onUpdateRecord: (record: PackageRecord) => void | Promise<void>;
  onBurnRecord: ReturnType<typeof useStudio>["burnRecordRights"];
  onAddPayment: ReturnType<typeof useStudio>["addPayment"];
  onRemovePayment: ReturnType<typeof useStudio>["removePayment"];
  onNewAppointment: (customerId: string) => void;
  onToast: (message: string) => void;
  initialMobileCredential?: CustomerAppCredential;
  payments: ReturnType<typeof useStudio>["payments"];
  ledger: ReturnType<typeof useStudio>["ledger"];
}) {
  const { paymentMethods, serviceTypes, refresh } = useStudio();
  const [tab, setTab] = useState<"profile" | "records" | "payments" | "ledger" | "app" | "registration">(
    initialMobileCredential?.customerId === customer.id ? "app" : "profile"
  );
  const [editing, setEditing] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<PackageRecord | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [burnRecord, setBurnRecord] = useState<PackageRecord | null>(null);
  const [paymentRecordId, setPaymentRecordId] = useState("");
  const [movementFilter, setMovementFilter] = useState<"All" | LedgerEntry["description"]>("All");
  const [historySummaryOpen, setHistorySummaryOpen] = useState(false);
  const [mobileCredential, setMobileCredential] = useState<CustomerAppCredential | null>(
    initialMobileCredential?.customerId === customer.id ? initialMobileCredential : null
  );
  const [mobileAccountSaving, setMobileAccountSaving] = useState(false);
  const [mobileSmsSending, setMobileSmsSending] = useState(false);
  const [mobileAccountError, setMobileAccountError] = useState("");
  const appAccount: Customer["appAccount"] = customer.appAccount ?? (mobileCredential ? {
    id: mobileCredential.accountId,
    email: mobileCredential.email,
    isActive: true,
    mustChangePassword: true,
    version: mobileCredential.version
  } : undefined);
  const customerRecords = records.filter((record) => record.customerId === customer.id);
  const customerPayments = payments.filter((payment) => payment.customerId === customer.id);
  const customerLedger = ledger.filter(
    (entry) => entry.customerId === customer.id && entry.description !== "Correction"
  );
  const paymentOptions = customerRecords.filter((record) => record.isOperational !== false).map((record) => {
    const recordLedger = customerLedger.find(
      (entry) =>
        entry.description === "Record" &&
        entry.reference === `R${record.id.replace(/\D/g, "")}`
    );
    return {
      record,
      remainingAmount:
        record.remainingAmount ??
        recordLedger?.remainingAmount ??
        record.price
    };
  });
  const selectedPaymentOption = paymentOptions.find((item) => item.record.id === paymentRecordId);
  useEffect(() => {
    if (!paymentOpen) return;
    const firstUnpaid = paymentOptions.find((item) => item.remainingAmount > 0) ?? paymentOptions[0];
    setPaymentRecordId(firstUnpaid?.record.id ?? "");
  }, [paymentOpen, customer.id, records, ledger]);
  const pilatesRecordCount = customerRecords.filter((record) => record.detail.includes("Pilates")).length;
  const loyalty = pilatesRecordCount >= 6 ? 10 : pilatesRecordCount >= 3 ? 5 : 0;

  useEffect(() => {
    if (initialMobileCredential?.customerId !== customer.id) return;
    setMobileCredential(initialMobileCredential);
    setTab("app");
  }, [customer.id, initialMobileCredential]);

  async function issueMobileCredential(reset: boolean) {
    if (!customer.email?.trim()) {
      setMobileAccountError("Önce müşteri kartına geçerli bir e-posta adresi ekleyin.");
      return;
    }
    if (reset && !window.confirm(`${customer.name} için mobil uygulama şifresi sıfırlansın mı? Açık mobil oturumlar kapatılacak.`)) return;
    setMobileAccountSaving(true);
    setMobileAccountError("");
    try {
      const response = await studioApi.command<CustomerAppCredential>(
        reset ? "RESET_MOBILE_PASSWORD" : "CREATE_MOBILE_ACCOUNT",
        {
          customerId: customer.id,
          ...(reset ? { version: appAccount?.version } : {})
        }
      );
      setMobileCredential(response.result);
      await refresh().catch(() => undefined);
      onToast(reset ? "Yeni geçici şifre hazırlandı." : "Mobil uygulama erişimi oluşturuldu.");
    } catch (error) {
      setMobileAccountError(studioErrorCopy(error, "Mobil uygulama erişimi hazırlanamadı."));
    } finally {
      setMobileAccountSaving(false);
    }
  }

  async function copyMobileCredential() {
    if (!mobileCredential) return;
    await navigator.clipboard.writeText(
      `The Heal Hub mobil uygulama\nE-posta: ${mobileCredential.email}\nGeçici şifre: ${mobileCredential.oneTimePassword}`
    );
    onToast("Mobil giriş bilgileri panoya kopyalandı.");
  }

  async function sendMobileCredentialSms() {
    if (!mobileCredential) return;
    setMobileSmsSending(true);
    setMobileAccountError("");
    try {
      await studioApi.command("SEND_SMS", {
        customerIds: [customer.id],
        message: `The Heal Hub mobil uygulama giriş bilgileriniz: E-posta: ${mobileCredential.email} Geçici şifre: ${mobileCredential.oneTimePassword} İlk girişte yeni şifrenizi belirleyin.`
      });
      onToast("Mobil giriş bilgileri SMS ile gönderildi.");
    } catch (error) {
      setMobileAccountError(studioErrorCopy(error, "SMS gönderilemedi. Bilgileri kopyalayıp müşteriye iletebilirsiniz."));
    } finally {
      setMobileSmsSending(false);
    }
  }

  return (
    <>
      <button className="modal-scrim" onClick={onClose} />
      <aside className="detail-drawer customer-detail-drawer" onKeyDown={(event) => { if (event.key === "Escape") onClose(); }}>
        <div className="drawer-head">
          <div><span className="eyebrow">{customer.id}</span><h2>Müşteri kartı</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Müşteri kartını kapat"><X size={20} /></button>
        </div>
        <div className="customer-hero">
          <span className="avatar xlarge">{customer.initials}</span>
          <div><h3>{customer.name}</h3><span>{customer.typeOf} · {customer.district}</span></div>
          <span className={`status ${customer.status === "active" ? "completed" : "cancelled"}`}>{customer.status === "active" ? "Aktif" : "Pasif"}</span>
        </div>
        <div className="quick-actions">
          <button onClick={() => onNewAppointment(customer.id)}><CalendarDays size={17} /><span>Randevu</span></button>
          <button onClick={() => setRecordOpen(true)}><PackagePlus size={17} /><span>Paket</span></button>
          <button onClick={() => { setTab("payments"); setPaymentOpen(true); }}><CircleDollarSign size={17} /><span>Ödeme</span></button>
        </div>
        <div className="drawer-tabs">
          <button className={tab === "profile" ? "active" : ""} onClick={() => setTab("profile")}>Bilgiler</button>
          <button className={tab === "records" ? "active" : ""} onClick={() => setTab("records")}>Paketler</button>
          <button className={tab === "payments" ? "active" : ""} onClick={() => setTab("payments")}>Ödemeler</button>
          <button className={tab === "ledger" ? "active" : ""} onClick={() => setTab("ledger")}>Hareketler</button>
          <button className={tab === "app" ? "active" : ""} onClick={() => setTab("app")}>App Bilgileri</button>
          <button className={tab === "registration" ? "active" : ""} onClick={() => setTab("registration")}>Kayıt Formu</button>
        </div>

        {tab === "profile" && (
          editing ? (
            <CustomerForm initial={customer} onCancel={() => setEditing(false)} onSave={async (value) => { await onUpdate(value); setEditing(false); onToast("Müşteri bilgileri güncellendi."); }} />
          ) : (
            <div className="detail-section">
              <div className="balance-strip">
                <div><span>Kalan seans</span><strong>{customer.remainingSessions}</strong></div>
                <div><span>Sonraki randevu</span><strong>{customer.nextAppointment}</strong></div>
                <div><span>Seviye</span><strong>{customerExperienceLevelLabel(customer.experienceLevel)}</strong></div>
              </div>
              <div className="info-list">
                <div><span>Telefon</span><strong>{customer.phone}</strong></div>
                <div><span>E-posta</span><strong>{customer.email || "—"}</strong></div>
                <div><span>Doğum tarihi</span><strong>{shortDate(customer.birthDate)}</strong></div>
                <div><span>Kayıt tarihi</span><strong>{shortDate(customer.registrationDate)}</strong></div>
                <div><span>Notlar</span><strong>{customer.notes || "Not eklenmemiş"}</strong></div>
                <div><span>Geçmiş</span><strong>{customer.history || "Geçmiş bilgisi yok"}</strong></div>
              </div>
              <div className="customer-preference-summary">
                <div className="customer-preference-summary-head">
                  <span>Seans eşleşme tercihleri</span>
                  <small>Programlama sırasında dikkate alınır</small>
                </div>
                <div className="customer-preference-summary-grid">
                  <div className={customer.requiresExclusiveSlot ? "restricted" : ""}>
                    <span>Yanına başka danışan</span>
                    <strong>{customer.requiresExclusiveSlot ? "İstemiyor" : "Uygun"}</strong>
                  </div>
                  <div className={customer.avoidsMaleCoAttendee ? "restricted" : ""}>
                    <span>Erkek danışanla aynı saat</span>
                    <strong>{customer.avoidsMaleCoAttendee ? "İstemiyor" : "Uygun"}</strong>
                  </div>
                </div>
              </div>
              <button className="button ghost wide" onClick={() => setEditing(true)}><Edit3 size={17} /> Müşteri kartını düzenle</button>
            </div>
          )
        )}

        {tab === "records" && (
          <div className="detail-section">
            <div className="loyalty-card"><div><span>Sadakat indirimi</span><strong>%{loyalty}</strong></div><p>Toplam {customerRecords.length} paket kaydı üzerinden otomatik hesaplanır.</p></div>
            {customerRecords.length ? customerRecords.map((record) => {
              const identity = packageIdentity(record, customerRecords, serviceTypes);
              const pendingAmount = Math.max(0, record.remainingAmount ?? record.price);
              const paidAmount = Math.max(0, record.paidAmount ?? record.price - pendingAmount);
              return (
              <div className={`mini-ledger-row package-record-row ${record.expiryDate && record.expiryDate < today() ? "expired" : ""}`} key={record.id}>
                <span className="record-icon"><PackagePlus size={16} /></span>
                <div>
                  <span className="package-identity-line">
                    <strong>{identity.serviceName}</strong>
                    {identity.sequence && <i>{identity.sequence}. paket</i>}
                  </span>
                  <span className="package-session-line">
                    <b>{record.quantity} seans</b>
                    <b>{record.remainingSessions ?? 0} kalan</b>
                    <span>{shortDate(record.registrationDate)} · Son kullanım {shortDate(record.expiryDate)}</span>
                  </span>
                  <span className={`package-payment-status ${pendingAmount > 0 ? "pending" : "paid"}`}>
                    {pendingAmount > 0
                      ? `${money(paidAmount)} ödendi · ${money(pendingAmount)} bekliyor`
                      : `Ödeme tamamlandı · ${money(paidAmount)} tahsil edildi`}
                  </span>
                  <span className="record-rule-line">
                    {record.isOperational === false && <i className="expired-chip">Arşiv kayıt · randevuya kapalı</i>}
                    <i>{Math.max(0, (record.cancellationAllowance ?? 0) - (record.cancellationsUsed ?? 0))} iptal hakkı kaldı</i>
                    {record.expiryDate && record.expiryDate < today() && <i className="expired-chip">Süresi doldu</i>}
                  </span>
                </div>
                <span className="record-row-actions">
                  <strong>{money(record.price)}</strong>
                  {record.expiryDate && record.expiryDate < today() && (record.remainingSessions ?? customerLedger.find((entry) => entry.reference === `R${record.id.replace(/\D/g, "")}`)?.remainingQuantity ?? 0) > 0 && <button className="button danger compact burn-rights-button" onClick={() => setBurnRecord(record)}>Hakları yak</button>}
                  {record.isOperational !== false && <button className="icon-button" onClick={() => setEditRecord(record)}><Edit3 size={15} /></button>}
                </span>
              </div>
              );
            }) : <div className="empty-state"><PackagePlus size={25} /><strong>Henüz paket yok</strong><span>İlk paket kaydını oluşturun.</span></div>}
            <button className="button secondary wide" onClick={() => setRecordOpen(true)}><Plus size={17} /> Yeni paket kaydı</button>
          </div>
        )}

        {tab === "payments" && (
          <div className="detail-section">
            <div className="balance-strip two"><div><span>Toplam ödeme</span><strong>{money(customerPayments.reduce((sum, item) => sum + item.amount, 0))}</strong></div><div><span>Bekleyen ödeme</span><strong>{money(customerLedger.filter((entry) => entry.description === "Record").reduce((sum, entry) => sum + (entry.remainingAmount ?? 0), 0))}</strong></div></div>
            {customerPayments.map((payment) => <div className="mini-ledger-row payment-mini-row" key={payment.id}><span className="record-icon cash"><CircleDollarSign size={16} /></span><div><strong>{payment.detail}</strong><span>{shortDate(payment.date)} · {payment.method}</span></div><strong>{money(payment.amount)}</strong><button className="icon-button danger-icon" aria-label="Ödemeyi sil" title="Hatalı ödeme kaydını sil" onClick={async () => { if (!window.confirm(`${payment.detail} için ${money(payment.amount)} tutarındaki ödeme silinsin mi? Paket tahsilatı geri alınacak.`)) return; await onRemovePayment(payment.id); onToast("Ödeme silindi; paket bakiyesi yeniden hesaplandı."); }}><Trash2 size={15} /></button></div>)}
            <button className="button primary wide" onClick={() => setPaymentOpen(true)}><Plus size={17} /> Ödeme kaydet</button>
          </div>
        )}

        {tab === "ledger" && (
          <div className="detail-section">
            <CustomerHistoryToolbar
              movementFilter={movementFilter}
              onMovementFilterChange={setMovementFilter}
              onOpenSummary={() => setHistorySummaryOpen(true)}
            />
            {customerLedger.filter((entry) => movementFilter === "All" || entry.description === movementFilter).map((entry) => <div className="mini-ledger-row" key={entry.id}><span className={`record-icon ${entry.description.toLowerCase()}`}><History size={16} /></span><div><strong>{ledgerTypeLabel(entry.description)} · {entry.relatedWith}</strong><span>{shortDate(entry.date)} · {entry.reference}</span></div><strong>{entry.quantity ? `${entry.quantity > 0 ? "+" : ""}${entry.quantity}` : money(entry.paymentAmount ?? 0)}</strong></div>)}
          </div>
        )}

        {tab === "app" && (
          <div className="detail-section customer-app-section">
            <div className={`app-access-hero ${appAccount?.isActive ? "active" : "pending"}`}>
              <span className="app-access-icon"><Smartphone size={24} /></span>
              <div>
                <span className="eyebrow">The Heal Hub mobil</span>
                <h3>{appAccount ? "Mobil erişim açık" : "Mobil erişim henüz oluşturulmadı"}</h3>
                <p>{appAccount ? "Danışan e-posta adresiyle uygulamaya giriş yapabilir." : "İlk giriş için tek kullanımlık geçici şifre oluşturun."}</p>
              </div>
              <span className={`status ${appAccount?.isActive ? "completed" : "cancelled"}`}>
                {appAccount?.isActive ? "Aktif" : "Kapalı"}
              </span>
            </div>

            <div className="app-account-facts">
              <div>
                <span>Giriş e-postası</span>
                <strong>{appAccount?.email || customer.email || "E-posta eklenmedi"}</strong>
              </div>
              <div>
                <span>Şifre durumu</span>
                <strong>{appAccount ? (appAccount.mustChangePassword ? "İlk girişte değişecek" : "Danışan tarafından belirlendi") : "Oluşturulmadı"}</strong>
              </div>
              <div>
                <span>Son giriş</span>
                <strong>{appAccount?.lastLoginAt ? shortDateTime(appAccount.lastLoginAt) : "Henüz giriş yapılmadı"}</strong>
              </div>
              <div>
                <span>Hesap güvenliği</span>
                <strong>{appAccount?.lockedUntilUtc && new Date(appAccount.lockedUntilUtc).getTime() > Date.now() ? "Geçici olarak kilitli" : "Girişe hazır"}</strong>
              </div>
            </div>

            {appAccount && customer.email && appAccount.email.toLocaleLowerCase("tr-TR") !== customer.email.toLocaleLowerCase("tr-TR") && (
              <div className="app-account-warning">
                <LockKeyhole size={17} />
                <span>Müşteri kartındaki e-posta ile mobil giriş e-postası farklı. Mobil giriş halen <strong>{appAccount.email}</strong> adresiyle çalışır.</span>
              </div>
            )}

            {!customer.email && !appAccount && (
              <div className="app-account-warning">
                <LockKeyhole size={17} />
                <span>Mobil erişim oluşturmak için önce Bilgiler sekmesinden müşterinin e-posta adresini kaydedin.</span>
              </div>
            )}

            {mobileCredential && (
              <div className="one-time-credential" role="status">
                <div className="one-time-credential-head">
                  <span><ShieldCheck size={19} /></span>
                  <div><strong>Geçici giriş bilgileri hazır</strong><small>Bu şifre yalnızca bu ekran açıkken gösterilir.</small></div>
                </div>
                <div className="credential-values">
                  <div><span>E-posta</span><strong>{mobileCredential.email}</strong></div>
                  <div><span>Geçici şifre</span><strong className="temporary-password">{mobileCredential.oneTimePassword}</strong></div>
                </div>
                <div className="credential-actions">
                  <button className="button ghost" type="button" onClick={copyMobileCredential}><Copy size={16} /> Bilgileri kopyala</button>
                  <button className="button secondary" type="button" disabled={mobileSmsSending || customer.dontSendSms || !customer.phone} onClick={sendMobileCredentialSms}><Send size={16} /> {mobileSmsSending ? "Gönderiliyor…" : "SMS ile gönder"}</button>
                </div>
                {(customer.dontSendSms || !customer.phone) && <small className="credential-sms-note">SMS göndermek için müşteri kartında geçerli telefon ve SMS izni bulunmalı.</small>}
              </div>
            )}

            {mobileAccountError && <div className="form-error">{mobileAccountError}</div>}

            <div className="app-security-note">
              <ShieldCheck size={18} />
              <span><strong>Şifre veritabanında açık tutulmaz.</strong> Geçici kod ilk girişte değiştirilir; sıfırlama işlemi açık mobil oturumları kapatır.</span>
            </div>

            {appAccount ? (
              <button className="button danger wide" type="button" disabled={mobileAccountSaving} onClick={() => issueMobileCredential(true)}>
                <RefreshCcw size={17} /> {mobileAccountSaving ? "Yeni şifre hazırlanıyor…" : "Şifreyi sıfırla ve yenisini oluştur"}
              </button>
            ) : (
              <button className="button primary wide" type="button" disabled={mobileAccountSaving || !customer.email} onClick={() => issueMobileCredential(false)}>
                <LockKeyhole size={17} /> {mobileAccountSaving ? "Erişim hazırlanıyor…" : "Mobil erişim oluştur"}
              </button>
            )}
          </div>
        )}

        {tab === "registration" && (
          <RegistrationFormPanel customer={customer} />
        )}
      </aside>
      {recordOpen && (
        <Modal title="Yeni paket kaydı" eyebrow={`${customer.name} · SQL fiyat kataloğu`} onClose={() => setRecordOpen(false)}>
          <PackageCreationForm
            customer={customer}
            customerRecords={customerRecords}
            onCancel={() => setRecordOpen(false)}
            onSave={async (record) => {
              await onAddRecord(record);
              setRecordOpen(false);
              onToast("Paket kaydı oluşturuldu; fiyat ve indirim bilgisi kaydedildi.");
            }}
          />
        </Modal>
      )}
      {editRecord && (
        <Modal title="Kayıt detayını değiştir" eyebrow={`${packageIdentity(editRecord, customerRecords, serviceTypes).label} · paket güncelleme`} onClose={() => setEditRecord(null)}>
          <PackageRecordUpdateForm
            record={editRecord}
            paymentMethods={paymentMethods}
            onCancel={() => setEditRecord(null)}
            onSave={async (updatedRecord) => {
              await onUpdateRecord(updatedRecord);
              setEditRecord(null);
              onToast("Paket fiyatı, seans adedi, bitiş tarihi, iptal hakkı ve ödeme yöntemi güncellendi.");
            }}
          />
        </Modal>
      )}
      {paymentOpen && (
        <Modal title="Ödeme kaydet" eyebrow={`${customer.name} · ${customer.id}`} onClose={() => setPaymentOpen(false)}>
          <form onSubmit={async (event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            await onAddPayment({
              id: `PAY-${String(Date.now()).slice(-4)}`,
              customerId: customer.id,
              recordId: selectedPaymentOption?.record.id,
              customer: customer.name,
              detail: selectedPaymentOption
                ? packageIdentity(selectedPaymentOption.record, customerRecords, serviceTypes).label
                : "",
              amount: Number(data.get("amount")),
              date: String(data.get("date")),
              method: String(data.get("method"))
            });
            setPaymentOpen(false);
            onToast("Ödeme pakete işlendi ve kalan tutar güncellendi.");
          }}>
            <label className="field full">
              <span>Hangi paket için?</span>
              <select value={paymentRecordId} onChange={(event) => setPaymentRecordId(event.target.value)} required>
                {paymentOptions.map(({ record, remainingAmount }) => (
                  <option value={record.id} key={record.id}>
                    {packageIdentity(record, customerRecords, serviceTypes).label} · {record.quantity} seans · {remainingAmount <= 0 ? "Ödendi" : `${money(remainingAmount)} kaldı`}
                  </option>
                ))}
              </select>
            </label>
            {selectedPaymentOption && selectedPaymentOption.remainingAmount <= 0 && paymentOptions.some((item) => item.remainingAmount > 0) && (
              <div className="paid-warning"><CheckCircle2 size={18} /><span><strong>Bu paket tamamen ödenmiş.</strong> Aynı pakete yeni ödeme eklenemez.</span></div>
            )}
            {selectedPaymentOption && selectedPaymentOption.remainingAmount > 0 && (
              <div className="payment-limit-note">Bu paket için en fazla <strong>{money(selectedPaymentOption.remainingAmount)}</strong> ödeme girebilirsiniz.</div>
            )}
            <div className="form-row"><label className="field"><span>Ödeme tutarı</span><input name="amount" type="number" min="1" max={selectedPaymentOption?.remainingAmount} required /></label><label className="field"><span>Ödeme tarihi</span><input name="date" type="date" defaultValue={today()} required /></label></div>
            <label className="field full"><span>Ödeme türü</span><select name="method">{paymentMethods.filter((method) => method.code !== "UNKNOWN").map((method) => <option value={method.code} key={method.code}>{method.name}</option>)}</select></label>
            {!customerRecords.length && <div className="form-error">Bu müşterinin ödeme bağlanabilecek bir paket kaydı yok.</div>}
            {!!customerRecords.length && paymentOptions.every((item) => item.remainingAmount <= 0) && <div className="paid-warning"><CheckCircle2 size={18} /><span><strong>Tüm paketler ödenmiş.</strong> Yeni ödeme kaydı gerekmiyor.</span></div>}
            <div className="modal-actions"><button type="button" className="button ghost" onClick={() => setPaymentOpen(false)}>Vazgeç</button><button className="button primary" disabled={!selectedPaymentOption || selectedPaymentOption.remainingAmount <= 0}>Ödemeyi kaydet</button></div>
          </form>
        </Modal>
      )}
      {burnRecord && (
        <Modal title="Kalan hakları kapat" eyebrow={`${packageIdentity(burnRecord, customerRecords, serviceTypes).label} · süresi dolmuş paket`} onClose={() => setBurnRecord(null)}>
          <div className="confirm-message"><Trash2 size={27} /><p>Bu paketin kalan seansları sıfırlanacak ve pakete bağlı planlı randevular iptal edilecek.</p></div>
          <div className="modal-actions">
            <button className="button ghost" onClick={() => setBurnRecord(null)}>Vazgeç</button>
            <button className="button danger" onClick={async () => { await onBurnRecord(burnRecord.id); setBurnRecord(null); onToast("Süresi geçmiş paketin kalan hakları kapatıldı."); }}>Evet, hakları yak</button>
          </div>
        </Modal>
      )}
      {historySummaryOpen && (
        <CustomerHistorySummary
          customer={customer}
          onClose={() => setHistorySummaryOpen(false)}
        />
      )}
    </>
  );
}

export function CustomerOverlay({
  customerId,
  onClose,
  onNewAppointment,
  onToast
}: {
  customerId: string;
  onClose: () => void;
  onNewAppointment: (customerId: string) => void;
  onToast: (message: string) => void;
}) {
  const {
    customers,
    records,
    payments,
    ledger,
    addRecord,
    updateRecord,
    addPayment,
    removePayment,
    burnRecordRights,
    updateCustomer
  } = useStudio();
  const customer = customers.find((item) => item.id === customerId);
  if (!customer) return null;
  return (
    <CustomerDetail
      customer={customer}
      records={records}
      payments={payments}
      ledger={ledger}
      onClose={onClose}
      onUpdate={updateCustomer}
      onAddRecord={addRecord}
      onUpdateRecord={updateRecord}
      onBurnRecord={burnRecordRights}
      onAddPayment={addPayment}
      onRemovePayment={removePayment}
      onNewAppointment={onNewAppointment}
      onToast={onToast}
    />
  );
}

export function FullCustomersPage({
  onNewAppointment,
  onToast,
  externalQuery = "",
  openCustomerId
}: {
  onNewAppointment: (customerId?: string) => void;
  onToast: (message: string) => void;
  externalQuery?: string;
  openCustomerId?: string | null;
}) {
  const { customers, records, payments, ledger, reversalLogs, studioClosureLogs, addRecord, updateRecord, addPayment, removePayment, burnRecordRights, createCustomer, updateCustomer } = useStudio();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [initialMobileCredential, setInitialMobileCredential] = useState<CustomerAppCredential | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState(externalQuery);
  const [filter, setFilter] = useState<"all" | "active" | "inactive" | "new" | "renew">("all");
  const [workspaceTab, setWorkspaceTab] = useState<"customers" | "ledger" | "reversals" | "prices">("customers");
  const [ledgerFilter, setLedgerFilter] = useState<"All" | LedgerEntry["description"]>("All");
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [reversalMonth, setReversalMonth] = useState(monthRange().key);
  const [logTab, setLogTab] = useState<"reversals" | "studio">("reversals");
  const [expandedClosureId, setExpandedClosureId] = useState<string | null>(null);
  const selected = customers.find((item) => item.id === selectedId);
  useEffect(() => setQuery(externalQuery), [externalQuery]);
  useEffect(() => {
    if (openCustomerId) {
      setWorkspaceTab("customers");
      setSelectedId(openCustomerId);
    }
  }, [openCustomerId]);
  const visible = customers.filter((customer) => {
    const searchOk = `${customer.id} ${customer.name} ${customer.phone}`.toLocaleLowerCase("tr-TR").includes(query.toLocaleLowerCase("tr-TR"));
    const filterOk =
      filter === "all" ||
      customer.status === filter ||
      (filter === "new" && (customer.registrationDate ?? "") >= monthRange().from && (customer.registrationDate ?? "") <= monthRange().to) ||
      (filter === "renew" && customer.remainingSessions < 4);
    return searchOk && filterOk;
  });
  const monthReversals = reversalLogs.filter((entry) => entry.occurredAt.slice(0, 7) === reversalMonth);
  const monthStudioClosures = studioClosureLogs.filter((entry) => entry.date.slice(0, 7) === reversalMonth);

  return (
    <>
      <Heading
        eyebrow={`${customers.length} müşteri · ${customers.filter((item) => item.status === "active").length} aktif`}
        title="Müşteriler"
        description="Kart bilgileri, paket kayıtları, kalan seans, ödeme ve hesap hareketleri aynı müşteri profilinde."
        action={<button className="button primary" onClick={() => setCreateOpen(true)}><UserPlus size={18} /> Yeni müşteri</button>}
      />
      <div className="module-tabs">
        <button className={workspaceTab === "customers" ? "active" : ""} onClick={() => setWorkspaceTab("customers")}>Müşteri listesi</button>
        <button className={workspaceTab === "ledger" ? "active" : ""} onClick={() => setWorkspaceTab("ledger")}>Tüm hareketler</button>
        <button className={workspaceTab === "reversals" ? "active" : ""} onClick={() => setWorkspaceTab("reversals")}>İşlem günlüğü</button>
        <button className={workspaceTab === "prices" ? "active" : ""} onClick={() => setWorkspaceTab("prices")}>Fiyat listesi</button>
      </div>
      {workspaceTab === "customers" && (
        <>
      <div className="summary-ribbon">
        <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}><span>Toplam</span><strong>{customers.length}</strong></button>
        <button className={filter === "active" ? "active" : ""} onClick={() => setFilter("active")}><span>Aktif</span><strong>{customers.filter((item) => item.status === "active").length}</strong></button>
        <button className={filter === "inactive" ? "active" : ""} onClick={() => setFilter("inactive")}><span>Pasif</span><strong>{customers.filter((item) => item.status === "inactive").length}</strong></button>
        <button className={filter === "new" ? "active" : ""} onClick={() => setFilter("new")}><span>Bu ay yeni</span><strong>{customers.filter((item) => (item.registrationDate ?? "") >= monthRange().from && (item.registrationDate ?? "") <= monthRange().to).length}</strong></button>
        <button className={filter === "renew" ? "active" : ""} onClick={() => setFilter("renew")}><span>Yenileme gereken</span><strong>{customers.filter((item) => item.remainingSessions < 4).length}</strong></button>
      </div>
      <div className="toolbar">
        <label className="inline-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="No, isim veya telefon ara" /></label>
      </div>
      <div className="data-panel customer-table">
        <div className="table-head full-customer-grid"><span>Müşteri</span><span>Aktif ders</span><span>Telefon</span><span>Kalan</span><span>Sadakat</span><span>Durum</span><span /></div>
        {visible.map((customer) => (
          <button className="table-row full-customer-grid" key={customer.id} onClick={() => setSelectedId(customer.id)}>
            <span className="person-cell"><i className="avatar tiny">{customer.initials}</i><span><strong>{customer.name}</strong><small>{customer.id}</small></span></span>
            <span>{customer.packageName}</span><span>{customer.phone}</span>
            <strong className={customer.remainingSessions < 4 ? "danger-text" : ""}>{customer.remainingSessions}</strong>
            <span>Seviye {records.filter((item) => item.customerId === customer.id).length}</span>
            <span><i className={`status ${customer.status === "active" ? "completed" : "cancelled"}`}>{customer.status === "active" ? "Aktif" : "Pasif"}</i></span>
            <ChevronRight size={18} />
          </button>
        ))}
      </div>
        </>
      )}
      {workspaceTab === "ledger" && (
        <>
          <div className="toolbar">
            <div className="segmented">{(["All", "Record", "Session", "Workshop", "Payment"] as const).map((item) => <button className={ledgerFilter === item ? "active" : ""} onClick={() => setLedgerFilter(item)} key={item}>{item === "All" ? "Tümü" : ledgerTypeLabel(item)}</button>)}</div>
            <label className="inline-search"><Search size={16} /><input value={ledgerQuery} onChange={(event) => setLedgerQuery(event.target.value)} placeholder="Müşteri, kayıt veya referans ara" /></label>
          </div>
          <div className="data-panel ledger-table">
            <div className="table-head ledger-grid"><span>Tür</span><span>Müşteri</span><span>İlişkili kayıt</span><span>Seans</span><span>Kalan</span><span>Fiyat</span><span>Ödeme</span><span>Kalan tutar</span><span>Tarih</span></div>
            {ledger.filter((entry) => entry.description !== "Correction" && (ledgerFilter === "All" || entry.description === ledgerFilter) && `${entry.customer} ${entry.customerId} ${entry.relatedWith} ${entry.reference}`.toLocaleLowerCase("tr-TR").includes(ledgerQuery.toLocaleLowerCase("tr-TR"))).map((entry) => <div className="table-row ledger-grid" key={entry.id}><span><i className={`ledger-type ${entry.description.toLowerCase()}`}>{ledgerTypeLabel(entry.description)}</i></span><span><strong>{entry.customer}</strong><small>{entry.customerId}</small></span><span>{entry.relatedWith}<small>{entry.reference}</small></span><span>{ledgerQuantityLabel(entry)}</span><strong>{entry.remainingQuantity ?? "—"}</strong><span>{entry.price ? money(entry.price) : "—"}</span><span>{entry.paymentAmount ? money(entry.paymentAmount) : "—"}</span><strong>{entry.remainingAmount ? money(entry.remainingAmount) : "—"}</strong><span>{shortDate(entry.date)}</span></div>)}
          </div>
        </>
      )}
      {workspaceTab === "reversals" && (
        <section className="reversal-workspace">
          <div className="reversal-toolbar">
            <div>
              <span className="eyebrow">Denetim ve işlem geçmişi</span>
              <h2>{monthCaption(reversalMonth, "long")} işlem günlüğü</h2>
              <p>Düzeltmeler ile toplu stüdyo kapanışları birbirinden ayrı ve ayrıntılı izlenir.</p>
            </div>
            <label className="field reversal-month"><span>Ay</span><input type="month" value={reversalMonth} onChange={(event) => setReversalMonth(event.target.value)} /></label>
          </div>
          <div className="segmented audit-log-tabs">
            <button className={logTab === "reversals" ? "active" : ""} onClick={() => setLogTab("reversals")}>Düzeltmeler</button>
            <button className={logTab === "studio" ? "active" : ""} onClick={() => setLogTab("studio")}>Stüdyo işlemleri</button>
          </div>
          {logTab === "reversals" ? (
            <div className="data-panel reversal-table">
              <div className="table-head reversal-grid"><span>İşlem</span><span>Müşteri</span><span>İlişkili paket</span><span>Açıklama</span><span>Tarih</span></div>
              {monthReversals.map((entry) => (
                <div className="table-row reversal-grid" key={entry.id}>
                  <span><i className={`reversal-action ${entry.entityType.toLowerCase()}`}>{entry.actionCode === "PAYMENT_DELETED" ? "Ödeme silindi" : entry.actionCode === "HOLD_DELETED" ? "Bloke silindi" : "Seans yeniden açıldı"}</i><small>{entry.entityLabel}</small></span>
                  <span><strong>{entry.customer}</strong><small>{entry.customerId ?? "—"}</small></span>
                  <span>{entry.relatedRecord ?? "—"}</span>
                  <span>{entry.summary}</span>
                  <span>{shortDateTime(entry.occurredAt)}<small>{entry.actor ?? "Personel web"}</small></span>
                </div>
              ))}
              {!monthReversals.length && <div className="empty-state compact"><History size={24} /><strong>Bu ay geri alınan işlem yok</strong><span>Silinen ödeme ve blokeler ile yeniden açılan seanslar burada görünür.</span></div>}
            </div>
          ) : (
            <div className="studio-audit-list">
              {monthStudioClosures.map((entry) => {
                const expanded = expandedClosureId === entry.id;
                return (
                  <article className={`studio-audit-card ${expanded ? "expanded" : ""}`} key={entry.id}>
                    <button className="studio-audit-summary" onClick={() => setExpandedClosureId(expanded ? null : entry.id)}>
                      <span className="studio-audit-icon"><CalendarDays size={18} /></span>
                      <span><strong>{shortDate(entry.date)} · Stüdyo kapanışı</strong><small>{entry.reason || "Açıklama girilmedi"} · {entry.actor || "THH Portal"}</small></span>
                      <span><strong>{entry.cancelledAppointments}</strong><small>randevu iptal</small></span>
                      <span><strong>{entry.cancelledHolds}</strong><small>bloke iptal</small></span>
                      <span><strong>{entry.extendedPackageCount}</strong><small>paket uzatıldı{entry.extendedDays ? ` · +${entry.extendedDays} gün` : ""}</small></span>
                      <ChevronDown size={18} />
                    </button>
                    {expanded && (
                      <div className="studio-audit-details">
                        <section>
                          <h4>İptal edilen randevular</h4>
                          {entry.appointments.map((appointment) => (
                            <div className="studio-audit-detail-row" key={appointment.id}>
                              <span><strong>{appointment.time} · {appointment.customer}</strong><small>{appointment.service}</small></span>
                              <span>{appointment.packageLabel || "Paket bağlantısı yok"}</span>
                            </div>
                          ))}
                          {!entry.appointments.length && <p>İptal edilen randevu yok.</p>}
                        </section>
                        <section>
                          <h4>Uzatılan paketler</h4>
                          {entry.packageExtensions.map((extension) => (
                            <div className="studio-audit-detail-row" key={extension.id}>
                              <span><strong>{extension.customer}</strong><small>{extension.packageLabel}</small></span>
                              <span>{shortDate(extension.previousExpiryDate)} → <strong>{shortDate(extension.newExpiryDate)}</strong> · +{extension.extendedDays} gün</span>
                            </div>
                          ))}
                          {!entry.packageExtensions.length && <p>Bu kapanış için paket süresi uzatılmadı.</p>}
                        </section>
                      </div>
                    )}
                  </article>
                );
              })}
              {!monthStudioClosures.length && <div className="empty-state compact"><CalendarDays size={24} /><strong>Bu ay stüdyo kapanışı yok</strong><span>Toplu iptal ve paket uzatma işlemleri burada görünür.</span></div>}
            </div>
          )}
        </section>
      )}
      {workspaceTab === "prices" && (
        <PriceCatalog />
      )}
      {createOpen && <Modal title="Yeni müşteri" eyebrow="Müşteri profili oluştur" onClose={() => setCreateOpen(false)} wide><CustomerForm onCancel={() => setCreateOpen(false)} onSave={async (customer) => {
        const created = await createCustomer(customer);
        setCreateOpen(false);
        setInitialMobileCredential(created.mobileCredential);
        setSelectedId(created.customerId);
        onToast(created.mobileAccountError ?? (created.mobileCredential ? `${customer.name} oluşturuldu; mobil giriş bilgileri hazır.` : `${customer.name} müşteri olarak oluşturuldu.`));
      }} /></Modal>}
      {selected && <CustomerDetail customer={selected} records={records} payments={payments} ledger={ledger} initialMobileCredential={initialMobileCredential?.customerId === selected.id ? initialMobileCredential : undefined} onClose={() => { setSelectedId(null); setInitialMobileCredential(undefined); }} onUpdate={updateCustomer} onAddRecord={addRecord} onUpdateRecord={updateRecord} onBurnRecord={burnRecordRights} onAddPayment={addPayment} onRemovePayment={removePayment} onNewAppointment={onNewAppointment} onToast={onToast} />}
    </>
  );
}

type FinanceTab = "overview" | "payments" | "ledger" | "prices" | "costs";

export function FullFinancePage({
  onToast,
  onOpenCustomer
}: {
  onToast: (message: string) => void;
  onOpenCustomer: (customerId: string) => void;
}) {
  const { customers, payments, ledger, records, expenses, paymentMethods, serviceTypes, addPayment, removePayment, createExpense, voidExpense } = useStudio();
  const [tab, setTab] = useState<FinanceTab>("overview");
  const [ledgerFilter, setLedgerFilter] = useState<"All" | LedgerEntry["description"]>("All");
  const [ledgerQuery, setLedgerQuery] = useState("");
  const [costOpen, setCostOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentCustomerId, setPaymentCustomerId] = useState(customers[0]?.id ?? "");
  const [financePaymentRecordId, setFinancePaymentRecordId] = useState("");
  const [dateFrom, setDateFrom] = useState(monthRange().from);
  const [dateTo, setDateTo] = useState(monthRange().to);
  const completedPayments = payments.filter((payment) => !payment.statusCode || payment.statusCode === "COMPLETED");
  const filteredPayments = completedPayments.filter((payment) => payment.date >= dateFrom && payment.date <= dateTo);
  const visibleLedger = ledger.filter((entry) => entry.description !== "Correction");
  const filteredLedger = visibleLedger.filter(
    (entry) =>
      (ledgerFilter === "All" || entry.description === ledgerFilter) &&
      `${entry.customer} ${entry.customerId} ${entry.relatedWith} ${entry.reference}`
        .toLocaleLowerCase("tr-TR")
        .includes(ledgerQuery.toLocaleLowerCase("tr-TR"))
  );
  const currentPeriod = monthRange();
  const previousPeriod = monthRange(-1);
  const currentMonthPayments = completedPayments.filter((payment) => payment.date >= currentPeriod.from && payment.date <= currentPeriod.to);
  const previousMonthPayments = completedPayments.filter((payment) => payment.date >= previousPeriod.from && payment.date <= previousPeriod.to);
  const currentCollections = currentMonthPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const previousCollections = previousMonthPayments.reduce((sum, payment) => sum + payment.amount, 0);
  const collectionChange = previousCollections > 0 ? ((currentCollections - previousCollections) / previousCollections) * 100 : null;
  const pendingCutoff = rollingYearStart();
  const pendingRecords = records.filter((record) =>
    record.isOperational !== false &&
    record.statusCode === "ACTIVE" &&
    record.registrationDate >= pendingCutoff &&
    (record.remainingAmount ?? 0) > 0
  );
  const pendingBalance = pendingRecords.reduce((sum, record) => sum + (record.remainingAmount ?? 0), 0);
  const pendingCustomerCount = new Set(pendingRecords.map((record) => record.customerId)).size;
  const activeRecords = records.filter((record) =>
    record.isOperational !== false &&
    record.statusCode === "ACTIVE" &&
    !record.isExpired &&
    (record.remainingSessions ?? 0) > 0
  );
  const availableSessions = activeRecords.reduce((sum, record) => sum + (record.remainingSessions ?? 0), 0);
  const currentMonthExpenses = expenses.filter((expense) => expense.date >= currentPeriod.from && expense.date <= currentPeriod.to);
  const currentExpensesTotal = currentMonthExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  const expenseByCategory = currentMonthExpenses.reduce<Record<string, number>>((totals, expense) => {
    totals[expense.product] = (totals[expense.product] ?? 0) + expense.amount;
    return totals;
  }, {});
  const highestExpense = Object.entries(expenseByCategory).sort((left, right) => right[1] - left[1])[0];
  const collectionSeries = Array.from({ length: 7 }, (_, index) => monthRange(index - 6)).map((period) => ({
    ...period,
    total: completedPayments
      .filter((payment) => payment.date >= period.from && payment.date <= period.to)
      .reduce((sum, payment) => sum + payment.amount, 0)
  }));
  const chartMaximum = Math.max(1, ...collectionSeries.map((item) => item.total));
  const financePaymentOptions = records.filter((record) => record.customerId === paymentCustomerId && record.isOperational !== false).map((record) => {
    const recordLedger = ledger.find(
      (entry) =>
        entry.description === "Record" &&
        entry.reference === `R${record.id.replace(/\D/g, "")}`
    );
    return {
      record,
      remainingAmount:
        record.remainingAmount ??
        recordLedger?.remainingAmount ??
        record.price
    };
  });
  const selectedFinancePayment = financePaymentOptions.find((item) => item.record.id === financePaymentRecordId);
  useEffect(() => {
    if (!paymentOpen) return;
    const firstUnpaid = financePaymentOptions.find((item) => item.remainingAmount > 0) ?? financePaymentOptions[0];
    setFinancePaymentRecordId(firstUnpaid?.record.id ?? "");
  }, [paymentOpen, paymentCustomerId, records, ledger]);

  async function addCost(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      await createExpense({
        category: String(data.get("product")),
        description: String(data.get("description")),
        amount: Number(data.get("amount")),
        date: String(data.get("date")),
        note: String(data.get("note") || "") || undefined
      });
      setCostOpen(false);
      onToast("Gider başarıyla kaydedildi.");
    } catch {
      onToast("Gider kaydedilemedi. Bilgileri kontrol edip yeniden deneyin.");
    }
  }

  return (
    <>
      <Heading
        eyebrow="Yetkili alan · ödemeler ve giderler"
        title="Kasa & paketler"
        description="Tahsilat, bekleyen ödeme, rapor ve giderleri yalnızca yetkili kullanıcılar yönetir."
        action={tab === "costs" ? <button className="button primary" onClick={() => setCostOpen(true)}><Plus size={18} /> Gider ekle</button> : <button className="button primary" onClick={() => setPaymentOpen(true)}><Plus size={18} /> Ödeme kaydet</button>}
      />
      <div className="module-tabs">
        {([["overview", "Genel bakış"], ["payments", "Ödemeler"], ["costs", "Giderler"]] as Array<[FinanceTab, string]>).map(([id, label]) => <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)}>{label}</button>)}
      </div>

      {tab === "overview" && (
        <>
          <div className="metric-grid">
            <article className="metric-card"><span>Bu ay tahsilat</span><strong>{money(currentCollections)}</strong><small className={collectionChange !== null && collectionChange >= 0 ? "positive" : "danger-text"}>{collectionChange === null ? "Önceki ay verisi yok" : `Geçen aya göre %${Math.abs(collectionChange).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} ${collectionChange >= 0 ? "↑" : "↓"}`}</small></article>
            <article className="metric-card"><span>Son 12 ay bekleyen</span><strong>{money(pendingBalance)}</strong><small>{pendingCustomerCount} müşteri · {pendingRecords.length} paket</small></article>
            <article className="metric-card"><span>Aktif paket</span><strong>{activeRecords.length}</strong><small>{availableSessions} kalan seans</small></article>
            <article className="metric-card accent"><span>Bu ay gider</span><strong>{money(currentExpensesTotal)}</strong><small>{currentMonthExpenses.length} gider hareketi</small></article>
          </div>
          <div className="finance-overview-grid">
            <section className="panel chart-panel">
              <div className="panel-heading"><div><span className="eyebrow">Aylık tahsilat · otomatik güncel</span><h3>Son 7 ay</h3></div><span className="finance-live-chip">Güncel</span></div>
              <div className="bar-chart">{collectionSeries.map((item) => <div key={item.key} title={`${monthCaption(item.key, "long")}: ${money(item.total)}`}><strong>{item.total ? money(item.total) : "—"}</strong><span style={{ height: `${Math.max(item.total ? 8 : 0, (item.total / chartMaximum) * 100)}%` }} /><small>{monthCaption(item.key)}</small></div>)}</div>
            </section>
            <section className="panel finance-alerts"><div className="panel-heading"><div><span className="eyebrow">Son 12 ay · {pendingRecords.length} paket</span><h3>Tahsilat takibi</h3></div></div>{pendingRecords.map((record) => <button key={record.id} onClick={() => onOpenCustomer(record.customerId)}><span className="record-icon cash"><WalletCards size={16} /></span><span><strong>{record.customer}</strong><small>{packageIdentity(record, records, serviceTypes).label} · {record.quantity} seans · {shortDate(record.registrationDate)}</small></span><strong>{money(record.remainingAmount ?? 0)}</strong></button>)}</section>
          </div>
        </>
      )}

      {tab === "payments" && (
        <>
          <div className="toolbar date-toolbar">
            <div className="date-range"><label>İlk gün<input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label><span>→</span><label>Son gün<input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label></div>
            <div className="payment-total"><span>Seçili dönem toplamı</span><strong>{money(filteredPayments.reduce((sum, item) => sum + item.amount, 0))}</strong></div>
          </div>
          <div className="data-panel">
            <div className="table-head payment-full-grid"><span>Müşteri</span><span>Paket / kayıt</span><span>Tarih</span><span>Yöntem</span><span>Tutar</span><span /></div>
            {filteredPayments.map((payment) => <div className="table-row payment-full-grid" key={payment.id}><strong>{payment.customer}</strong><span>{payment.detail}</span><span>{shortDate(payment.date)}</span><span>{payment.method}</span><strong>{money(payment.amount)}</strong><button className="icon-button danger-icon" aria-label="Ödemeyi sil" title="Hatalı ödeme kaydını sil" onClick={async () => { if (!window.confirm(`${payment.customer} için ${money(payment.amount)} tutarındaki ödeme silinsin mi? Paket tahsilatı geri alınacak.`)) return; await removePayment(payment.id); onToast("Ödeme silindi; paket bakiyesi yeniden hesaplandı."); }}><Trash2 size={15} /></button></div>)}
          </div>
        </>
      )}

      {tab === "ledger" && (
        <>
          <div className="toolbar"><div className="segmented">{(["All", "Record", "Session", "Workshop", "Payment"] as const).map((item) => <button className={ledgerFilter === item ? "active" : ""} onClick={() => setLedgerFilter(item)} key={item}>{item === "All" ? "Tümü" : ledgerTypeLabel(item)}</button>)}</div><label className="inline-search"><Search size={16} /><input value={ledgerQuery} onChange={(event) => setLedgerQuery(event.target.value)} placeholder="Müşteri no, ad veya kayıt no" /></label></div>
          <div className="data-panel ledger-table">
            <div className="table-head ledger-grid"><span>Tür</span><span>Müşteri</span><span>İlişkili kayıt</span><span>Seans</span><span>Kalan</span><span>Fiyat</span><span>Ödeme</span><span>Kalan tutar</span><span>Tarih</span></div>
            {filteredLedger.map((entry) => <div className="table-row ledger-grid" key={entry.id}><span><i className={`ledger-type ${entry.description.toLowerCase()}`}>{ledgerTypeLabel(entry.description)}</i></span><span><strong>{entry.customer}</strong><small>{entry.customerId}</small></span><span>{entry.relatedWith}<small>{entry.reference}</small></span><span>{ledgerQuantityLabel(entry)}</span><strong>{entry.remainingQuantity ?? "—"}</strong><span>{entry.price ? money(entry.price) : "—"}</span><span>{entry.paymentAmount ? money(entry.paymentAmount) : "—"}</span><strong>{entry.remainingAmount ? money(entry.remainingAmount) : "—"}</strong><span>{shortDate(entry.date)}</span></div>)}
          </div>
        </>
      )}

      {tab === "prices" && (
        <PriceCatalog />
      )}

      {tab === "costs" && (
        <>
          <div className="metric-grid cost-metrics"><article className="metric-card"><span>Bu ay toplam gider</span><strong>{money(currentExpensesTotal)}</strong><small>{currentMonthExpenses.length} hareket</small></article><article className="metric-card"><span>En yüksek kategori</span><strong>{highestExpense?.[0] ?? "Kayıt yok"}</strong><small>{money(highestExpense?.[1] ?? 0)}</small></article></div>
          <div className="data-panel"><div className="table-head cost-grid"><span>Tarih</span><span>Kategori</span><span>Açıklama</span><span>Tutar</span><span /></div>{expenses.map((cost) => <div className="table-row cost-grid" key={cost.id}><span>{shortDate(cost.date)}</span><strong>{cost.product}</strong><span>{cost.description ?? "—"}</span><strong>{money(cost.amount)}</strong><button className="icon-button" onClick={async () => { if (!window.confirm("Bu gider kaydını geçersiz kılmak istiyor musun?")) return; await voidExpense(cost); onToast("Gider geçersiz kılındı."); }}><Trash2 size={16} /></button></div>)}</div>
        </>
      )}
      {costOpen && <Modal title="Gider kaydı" eyebrow="Kasa hareketi ekle" onClose={() => setCostOpen(false)}><form onSubmit={addCost}><label className="field full"><span>Kategori</span><select name="product">{costCategories.map((item) => <option key={item}>{item}</option>)}</select></label><label className="field full"><span>Açıklama</span><input name="description" required maxLength={300} placeholder="Örn. Temmuz ayı elektrik faturası" /></label><div className="form-row"><label className="field"><span>Tarih</span><input name="date" type="date" defaultValue={today()} required /></label><label className="field"><span>Tutar</span><input name="amount" type="number" min="1" step="0.01" required /></label></div><label className="field full"><span>Not <i>isteğe bağlı</i></span><input name="note" maxLength={1000} /></label><div className="modal-actions"><button type="button" className="button ghost" onClick={() => setCostOpen(false)}>Vazgeç</button><button className="button primary">Gideri kaydet</button></div></form></Modal>}
      {paymentOpen && (
        <Modal title="Ödeme kaydı" eyebrow="Paket ödemesi ekle" onClose={() => setPaymentOpen(false)}>
          <form onSubmit={async (event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            const customer = customers.find((item) => item.id === paymentCustomerId);
            if (!customer || !selectedFinancePayment || selectedFinancePayment.remainingAmount <= 0) return;
            await addPayment({
              id: `PAY-${String(Date.now()).slice(-4)}`,
              customerId: customer.id,
              recordId: selectedFinancePayment.record.id,
              customer: customer.name,
              detail: packageIdentity(selectedFinancePayment.record, records, serviceTypes).label,
              amount: Number(data.get("amount")),
              date: String(data.get("date")),
              method: String(data.get("method"))
            });
            setPaymentOpen(false);
            onToast("Ödeme kaydedildi ve paketin kalan tutarı güncellendi.");
          }}>
            <label className="field full"><span>Müşteri</span><select value={paymentCustomerId} onChange={(event) => setPaymentCustomerId(event.target.value)}>{customers.map((item) => <option value={item.id} key={item.id}>{item.name} · {item.id}</option>)}</select></label>
            <label className="field full"><span>İlgili paket</span><select value={financePaymentRecordId} onChange={(event) => setFinancePaymentRecordId(event.target.value)} required>{financePaymentOptions.map(({ record, remainingAmount }) => <option value={record.id} key={record.id}>{packageIdentity(record, records, serviceTypes).label} · {record.quantity} seans · {remainingAmount <= 0 ? "Ödendi" : `${money(remainingAmount)} kaldı`}</option>)}</select></label>
            {selectedFinancePayment && selectedFinancePayment.remainingAmount > 0 && <div className="payment-limit-note">Bu paket için en fazla <strong>{money(selectedFinancePayment.remainingAmount)}</strong> ödeme girebilirsiniz.</div>}
            {selectedFinancePayment && selectedFinancePayment.remainingAmount <= 0 && financePaymentOptions.some((item) => item.remainingAmount > 0) && <div className="paid-warning"><CheckCircle2 size={18} /><span><strong>Bu paket tamamen ödenmiş.</strong> Aynı pakete yeni ödeme eklenemez.</span></div>}
            {!!financePaymentOptions.length && financePaymentOptions.every((item) => item.remainingAmount <= 0) && <div className="paid-warning"><CheckCircle2 size={18} /><span><strong>Tüm paketler ödenmiş.</strong> Yeni ödeme kaydı gerekmiyor.</span></div>}
            <div className="form-row"><label className="field"><span>Tutar</span><input name="amount" type="number" min="1" max={selectedFinancePayment?.remainingAmount} required /></label><label className="field"><span>Tarih</span><input name="date" type="date" defaultValue={today()} /></label></div>
            <label className="field full"><span>Ödeme türü</span><select name="method">{paymentMethods.filter((method) => method.code !== "UNKNOWN").map((method) => <option value={method.code} key={method.code}>{method.name}</option>)}</select></label>
            {!financePaymentOptions.length && <div className="form-error">Seçilen müşterinin ödeme bağlanabilecek paketi yok.</div>}
            <div className="availability-note"><CircleDollarSign size={18} /><span><strong>Birlikte işlenecek:</strong> Ödeme, ilgili paket hareketi ve kalan tutar aynı anda güncellenecek.</span></div>
            <div className="modal-actions"><button type="button" className="button ghost" onClick={() => setPaymentOpen(false)}>Vazgeç</button><button className="button primary" disabled={!selectedFinancePayment || selectedFinancePayment.remainingAmount <= 0}>Ödemeyi kaydet</button></div>
          </form>
        </Modal>
      )}
    </>
  );
}

const opportunityStatuses = ["Dönüş Yapılacak", "Dönüş Bekleniyor", "Deneme Seansına Gelicek", "Kayıt Yapıldı"];

function OpportunityForm({ initial, onClose, onSave }: { initial?: Opportunity; onClose: () => void; onSave: (item: Opportunity) => Promise<void> }) {
  const { staff, serviceTypes } = useStudio();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const ownerId = String(data.get("ownerId") || "");
    const owner = staff.find((item) => item.id === ownerId)?.name ?? "Atanmadı";
    setSaving(true);
    setError("");
    try {
      await onSave({
        id: initial?.id ?? "",
        name: String(data.get("name")),
        phone: String(data.get("phone")),
        request: String(data.get("request")),
        status: String(data.get("status")),
        ownerId,
        owner,
        contactDate: String(data.get("contactDate")),
        type: String(data.get("type")),
        communicationType: String(data.get("communicationType")),
        notes: String(data.get("notes")),
        version: initial?.version
      });
    } catch {
      setError("Fırsat kaydedilemedi. Bilgileri kontrol edip yeniden deneyin.");
    } finally {
      setSaving(false);
    }
  }
  return <form onSubmit={submit}><div className="form-row"><label className="field"><span>Ad soyad</span><input name="name" defaultValue={initial?.name} required /></label><label className="field"><span>Telefon</span><input name="phone" defaultValue={initial?.phone} required /></label></div><div className="form-row"><label className="field"><span>İletişim tarihi</span><input name="contactDate" type="date" defaultValue={initial?.contactDate ?? today()} required /></label><label className="field"><span>İletişim türü</span><select name="communicationType" defaultValue={initial?.communicationType ?? "Telefon"}>{["Telefon", "Instagram", "Yüzyüze", "Diğer"].map((item) => <option key={item}>{item}</option>)}</select></label></div><div className="form-row"><label className="field"><span>İlgilendiği hizmet</span><select name="type" defaultValue={initial?.type ?? serviceTypes[0]?.name ?? ""}>{serviceTypes.map((item) => <option key={item.code}>{item.name}</option>)}</select></label><label className="field"><span>Satış sorumlusu</span><select name="ownerId" defaultValue={initial?.ownerId ?? staff[0]?.id ?? ""} required>{staff.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label></div><label className="field full"><span>Talep</span><input name="request" defaultValue={initial?.request} /></label><label className="field full"><span>Aşama</span><select name="status" defaultValue={initial?.status ?? opportunityStatuses[0]}>{opportunityStatuses.map((item) => <option key={item}>{item}</option>)}</select></label><label className="field full"><span>Notlar</span><textarea name="notes" rows={3} defaultValue={initial?.notes} /></label>{error && <div className="form-error">{error}</div>}<div className="modal-actions"><button type="button" className="button ghost" onClick={onClose}>Vazgeç</button><button className="button primary" disabled={saving}>{saving ? "Kaydediliyor…" : initial ? "Fırsatı güncelle" : "Fırsatı oluştur"}</button></div></form>;
}

export function FullOpportunitiesPage({ onToast, onOpenCustomer }: { onToast: (message: string) => void; onOpenCustomer: (customerId: string) => void }) {
  const { customers, opportunities, createOpportunity, updateOpportunity, archiveOpportunity } = useStudio();
  const [tab, setTab] = useState<"pipeline" | "low">("pipeline");
  const [editing, setEditing] = useState<Opportunity | "new" | null>(null);
  const lowCustomers = customers.filter(
    (item) => item.status === "active" && item.remainingSessions > 0 && item.remainingSessions < 4
  );
  return (
    <>
      <Heading eyebrow="Satış ve yenileme takibi" title="Fırsatlar" description="Yeni talepler ile seansı azalan mevcut müşterileri aynı takip sürecinde yönetin." action={<button className="button primary" onClick={() => setEditing("new")}><Plus size={18} /> Yeni fırsat</button>} />
      <div className="module-tabs"><button className={tab === "pipeline" ? "active" : ""} onClick={() => setTab("pipeline")}>Satış akışı <i>{opportunities.length}</i></button><button className={tab === "low" ? "active" : ""} onClick={() => setTab("low")}>Azalan seanslar <i>{lowCustomers.length}</i></button></div>
      {tab === "pipeline" ? <div className="opportunity-board four-columns">{opportunityStatuses.map((status) => <section className="board-column" key={status}><div className="board-head"><h3>{status}</h3><span>{opportunities.filter((item) => item.status === status).length}</span></div>{opportunities.filter((item) => item.status === status).map((item) => <article className="opportunity-card clickable" key={item.id} onClick={() => setEditing(item)}><span className="eyebrow">{item.id.slice(0, 8)} · {item.communicationType}</span><h3>{item.name}</h3><p>{item.request}</p><div><span>{item.phone}</span><i>{item.owner}</i></div></article>)}<button className="board-add" onClick={() => setEditing("new")}><Plus size={16} /> Kart ekle</button></section>)}</div> : <div className="data-panel"><div className="table-head low-session-grid"><span>Müşteri</span><span>Aktif ders</span><span>Telefon</span><span>Kalan seans</span><span /></div>{lowCustomers.map((customer) => <button className="table-row low-session-grid" key={customer.id} onClick={() => onOpenCustomer(customer.id)}><span className="person-cell"><i className="avatar tiny">{customer.initials}</i><strong>{customer.name}</strong></span><span>{customer.packageName}</span><span>{customer.phone}</span><strong className="danger-text">{customer.remainingSessions}</strong><span className="text-button">Müşteri kartını aç <ChevronRight size={15} /></span></button>)}</div>}
      {editing && <Modal title={editing === "new" ? "Yeni fırsat" : "Fırsat kartı"} eyebrow="Satış takibi" onClose={() => setEditing(null)} wide><OpportunityForm initial={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} onSave={async (value) => { if (editing === "new") await createOpportunity(value); else await updateOpportunity(value); setEditing(null); onToast(editing === "new" ? "Fırsat kartı oluşturuldu." : "Fırsat güncellendi."); }} />{editing !== "new" && <div className="destructive-zone"><button className="text-button danger-text" onClick={async () => { if (!window.confirm("Bu fırsatı kapatmak istiyor musun?")) return; await archiveOpportunity(editing); setEditing(null); onToast("Fırsat kapatıldı."); }}><Trash2 size={15} /> Fırsatı kapat</button></div>}</Modal>}
    </>
  );
}

function StudioAnnouncementComposer({ onToast }: { onToast: (message: string) => void }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetScreen, setTargetScreen] = useState<StudioAnnouncementTarget>("notifications");
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [lastSend, setLastSend] = useState<StudioAnnouncementResult | null>(null);
  const attemptKey = useRef<string | null>(null);
  const sendingGuard = useRef(false);
  const canSubmit = Boolean(title.trim() && message.trim());
  const targetLabel = studioAnnouncementTargets.find((item) => item.value === targetScreen)?.label;
  const confirmationRows = confirm
    ? studioAnnouncementReviewRows({ title, message, targetScreen })
    : [];

  function openConfirmation() {
    setSendError("");
    try {
      normalizeStudioAnnouncement({ title, message, targetScreen });
      attemptKey.current = resolveStudioAnnouncementAttemptKey(attemptKey.current);
      setConfirm(true);
    } catch (error) {
      setSendError(announcementErrorCopy(error));
    }
  }

  async function submitAnnouncement() {
    if (sendingGuard.current) return;
    sendingGuard.current = true;
    setSending(true);
    setSendError("");
    try {
      const payload = normalizeStudioAnnouncement({ title, message, targetScreen });
      const response = await studioApi.command<StudioAnnouncementResult>(
        "SEND_STUDIO_ANNOUNCEMENT",
        payload,
        attemptKey.current ??= crypto.randomUUID()
      );
      setLastSend(response.result);
      attemptKey.current = null;
      setConfirm(false);
      onToast(`${response.result.recipientCount} müşteriye mobil duyuru kuyruğa alındı.`);
    } catch (error) {
      setSendError(announcementErrorCopy(error));
    } finally {
      sendingGuard.current = false;
      setSending(false);
    }
  }

  return (
    <>
      <div className="announcement-workspace">
        <section className="panel announcement-composer">
          <div className="panel-heading">
            <div><span className="eyebrow">Push bildirimi</span><h3>Duyuruyu hazırla</h3></div>
            <Megaphone size={22} aria-hidden="true" />
          </div>
          <div className="announcement-form">
            <label className="field full">
              <span>Başlık <i>{title.length}/100</i></span>
              <input value={title} onChange={(event) => { setTitle(event.target.value); attemptKey.current = null; }} maxLength={100} placeholder="Örn. Yaz paketlerinde fırsat" />
            </label>
            <label className="field full">
              <span>Mesaj <i>{message.length}/500</i></span>
              <textarea value={message} onChange={(event) => { setMessage(event.target.value); attemptKey.current = null; }} rows={5} maxLength={500} placeholder="Müşterinin telefonunda göreceği kısa ve net metni yazın." />
            </label>
            <label className="field full">
              <span>Bildirime dokununca açılacak ekran</span>
              <select value={targetScreen} onChange={(event) => { setTargetScreen(event.target.value as StudioAnnouncementTarget); attemptKey.current = null; }}>
                {studioAnnouncementTargets.map((target) => <option key={target.value} value={target.value}>{target.label}</option>)}
              </select>
            </label>
          </div>
          {lastSend && <div className="sms-send-summary"><CheckCircle2 size={18} /><span><strong>{lastSend.recipientCount} mobil duyuru kuyruğa alındı.</strong> Kampanya güvenli biçimde tekilleştirildi.</span></div>}
          {sendError && !confirm && <div className="form-error">{sendError}</div>}
          <button className="button primary wide" disabled={!canSubmit || sending} onClick={openConfirmation}><Send size={17} /> Duyuruyu gözden geçir</button>
        </section>

        <aside className="panel push-preview-panel">
          <span className="eyebrow">Telefon önizleme</span>
          <div className="push-phone-preview">
            <div className="push-preview-status"><span>9:41</span><span>● ● ●</span></div>
            <div className="push-preview-card">
              <div className="push-preview-brand"><i>THH</i><span><strong>The Heal Hub</strong><small>şimdi</small></span></div>
              <h4>{title.trim() || "Duyuru başlığı"}</h4>
              <p>{message.trim() || "Müşteriniz bildirimin kısa metnini burada görecek."}</p>
            </div>
          </div>
          <div className="announcement-audience-note">
            <ShieldCheck size={20} />
            <p><strong>İzinli kitle</strong> Yalnızca aktif müşterilerden “Stüdyo haberleri” seçeneği açık olanlara gönderilir.</p>
          </div>
          <div className="announcement-target-note"><Smartphone size={18} /><span>Dokununca <strong>{targetLabel}</strong> açılır.</span></div>
        </aside>
      </div>

      {confirm && <Modal title="Mobil duyuruyu gönder" eyebrow="Son kontrol" onClose={() => { if (!sending) setConfirm(false); }}>
        <div className="confirm-message"><Megaphone size={28} /><p>“<strong>{title.trim()}</strong>” duyurusu aktif ve stüdyo haberlerine izin veren tüm mobil müşterilere gönderilecek.</p></div>
        <div className="announcement-confirmation-details">
          {confirmationRows.map((row) => <div key={row.label}><span>{row.label}</span><strong>{row.value}</strong></div>)}
        </div>
        <div className="payment-limit-note">Alıcı sayısı gönderim anında veritabanından hesaplanır. Aynı onay iki kez işlense bile müşteriye ikinci bildirim oluşturulmaz.</div>
        {sendError && <div className="form-error">{sendError}</div>}
        <div className="modal-actions"><button className="button ghost" disabled={sending} onClick={() => setConfirm(false)}>Geri dön</button><button className="button primary" disabled={sending} onClick={() => void submitAnnouncement()}>{sending ? "Kuyruğa alınıyor…" : "Duyuruyu gönder"}</button></div>
      </Modal>}
    </>
  );
}

interface SmsIntegrationStatus {
  provider: "ClickSend";
  status: "ready" | "not_configured" | "protection_required" | "unavailable";
  configured: boolean;
  balance: number | null;
  currency: string | null;
}

interface SmsSendResult {
  requestedCount: number;
  acceptedCount: number;
  queuedCount: number;
  blockedCount: number;
  totalPrice: number;
  currency: string | null;
  skipped: Array<{ customerId: string; name?: string; reason: string }>;
}

function smsErrorCopy(error: unknown) {
  const code = error instanceof Error ? error.message : "";
  if (code.includes("SMS_NOT_CONFIGURED")) return "ClickSend API bilgileri henüz tanımlanmadı.";
  if (code.includes("SMS_NO_VALID_RECIPIENTS")) return "Seçilen müşteriler arasında SMS gönderilebilecek geçerli bir telefon bulunamadı.";
  if (code.includes("SMS_RECIPIENT_LIMIT_EXCEEDED")) return "Tek seferde en fazla 500 alıcı seçilebilir.";
  if (code.includes("SMS_MESSAGE_TOO_LONG")) return "Mesaj 459 karakterden uzun olamaz.";
  if (code.includes("SMS_DUPLICATE_IN_PROGRESS")) return "Aynı SMS gönderimi zaten işleniyor. Birkaç saniye bekleyin; tekrar gönderilmeyecek.";
  if (code.includes("SMS_DELIVERY_STATUS_UNKNOWN")) return "ClickSend yanıtı kesinleşmediği için aynı mesaj güvenlik amacıyla tekrar gönderilmedi. Gönderim geçmişini kontrol edin.";
  if (error instanceof StudioApiError && error.code.includes("SMS_PROVIDER_")) {
    return error.message && !error.message.includes("SMS_PROVIDER_")
      ? `ClickSend gönderimi reddetti: ${error.message}`
      : "ClickSend gönderimi kabul etmedi. Hesap ve bakiye bilgilerini kontrol edin.";
  }
  return "SMS gönderilemedi. Bağlantıyı kontrol edip yeniden deneyin.";
}

export function FullMessagesPage({ onToast }: { onToast: (message: string) => void }) {
  const { customers } = useStudio();
  const [channel, setChannel] = useState<"sms" | "push">("sms");
  const [message, setMessage] = useState(messageTemplates.Hatırlatma);
  const canReceiveSms = (customer: Customer) =>
    !customer.dontSendSms && smsPhoneState(customer.phone) === "valid";
  const [selected, setSelected] = useState(() => new Set(customers.filter((item) => item.status === "active" && canReceiveSms(item)).map((item) => item.id)));
  const [confirm, setConfirm] = useState(false);
  const [recipientQuery, setRecipientQuery] = useState("");
  const [integration, setIntegration] = useState<SmsIntegrationStatus | null>(null);
  const [integrationLoading, setIntegrationLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [lastSend, setLastSend] = useState<SmsSendResult | null>(null);
  const smsAttemptKey = useRef<string | null>(null);
  const sendingGuard = useRef(false);
  const toggle = (id: string) => setSelected((current) => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const visibleRecipients = customers.filter((customer) =>
    `${customer.name} ${customer.phone} ${customer.id}`
      .toLocaleLowerCase("tr-TR")
      .includes(recipientQuery.toLocaleLowerCase("tr-TR"))
  );
  const selectedCustomers = customers.filter((customer) => selected.has(customer.id));
  const sendableCustomers = selectedCustomers.filter(canReceiveSms);
  const invalidSelectedCount = selectedCustomers.filter(
    (customer) => !customer.dontSendSms && smsPhoneState(customer.phone) === "invalid"
  ).length;
  const missingPhoneCount = selectedCustomers.filter(
    (customer) => smsPhoneState(customer.phone) === "missing"
  ).length;
  const permissionBlockedCount = selectedCustomers.filter((customer) => customer.dontSendSms).length;

  async function refreshIntegration() {
    setIntegrationLoading(true);
    try {
      const response = await studioApi.command<SmsIntegrationStatus>("GET_SMS_STATUS", {});
      setIntegration(response.result);
    } catch {
      setIntegration({
        provider: "ClickSend",
        status: "unavailable",
        configured: true,
        balance: null,
        currency: null
      });
    } finally {
      setIntegrationLoading(false);
    }
  }

  useEffect(() => {
    void refreshIntegration();
  }, []);

  async function submitSms() {
    if (sendingGuard.current) return;
    sendingGuard.current = true;
    setSending(true);
    setSendError("");
    try {
      const response = await studioApi.command<SmsSendResult>(
        "SEND_SMS",
        {
          customerIds: sendableCustomers.map((customer) => customer.id),
          message
        },
        smsAttemptKey.current ??= crypto.randomUUID()
      );
      setLastSend(response.result);
      smsAttemptKey.current = null;
      setConfirm(false);
      onToast(`${response.result.queuedCount} SMS ClickSend gönderim kuyruğuna alındı.`);
      await refreshIntegration();
    } catch (error) {
      setSendError(smsErrorCopy(error));
    } finally {
      sendingGuard.current = false;
      setSending(false);
    }
  }

  const integrationReady = integration?.status === "ready";
  const integrationLabel = integrationLoading
    ? "Kontrol ediliyor"
    : integrationReady
      ? integration?.balance == null
        ? "Bağlantı hazır"
        : `${integration.balance.toFixed(2)} ${integration.currency ?? ""}`.trim()
      : integration?.status === "not_configured"
        ? "Kurulum gerekli"
        : integration?.status === "protection_required"
          ? "Gönderim koruması gerekli"
        : "Bağlantı sorunu";

  const channelTabs = (
    <div className="module-tabs message-channel-tabs" aria-label="Mesaj kanalı">
      <button className={channel === "sms" ? "active" : ""} onClick={() => setChannel("sms")}><MessageSquareText size={16} /> SMS</button>
      <button className={channel === "push" ? "active" : ""} onClick={() => setChannel("push")}><Megaphone size={16} /> Mobil duyuru</button>
    </div>
  );

  if (channel === "push") {
    return (
      <>
        <Heading
          eyebrow="The Heal Hub · mobil bildirim"
          title="Mesaj merkezi"
          description="Kampanya ve stüdyo haberlerini izin veren aktif müşterilerin telefonlarına gönderin."
          action={<div className="announcement-ready-badge"><ShieldCheck size={18} /><span><small>Gönderim kanalı</small><strong>İzin kontrollü mobil push</strong></span></div>}
        />
        {channelTabs}
        <StudioAnnouncementComposer onToast={onToast} />
      </>
    );
  }

  return (
    <>
      <Heading
        eyebrow={`ClickSend · ${integrationReady ? "canlı bağlantı" : "güvenli SMS servisi"}`}
        title="Mesaj merkezi"
        description={
          integrationReady
            ? "SMS izni bulunan müşterilere ClickSend üzerinden güvenli toplu mesaj gönderin."
            : integration?.status === "protection_required"
              ? "Çift gönderimi engelleyen SQL koruması tamamlandığında mesaj gönderimi açılacak."
              : "Mesaj ekranı hazır. Canlı gönderim için ClickSend API bilgilerini backend ortamına ekleyin."
        }
        action={
          <div className={`sms-balance ${integrationReady ? "integration-ready" : "integration-waiting"}`}>
            <span>{integrationReady && integration?.balance != null ? "ClickSend bakiyesi" : "Gönderim durumu"}</span>
            <strong>{integrationLabel}</strong>
            <button type="button" onClick={() => void refreshIntegration()} disabled={integrationLoading}>Yenile</button>
          </div>
        }
      />
      {channelTabs}
      <div className="message-workspace">
        <section className="panel composer-panel">
          <div className="panel-heading"><div><span className="eyebrow">Mesaj içeriği</span><h3>SMS oluştur</h3></div><span className="character-count">{message.length}/459</span></div>
          <div className="template-list">{Object.entries(messageTemplates).map(([name, value]) => <button key={name} onClick={() => setMessage(value)} className={message === value ? "active" : ""}>{name}</button>)}</div>
          <textarea className="message-editor" value={message} onChange={(event) => setMessage(event.target.value)} rows={7} maxLength={459} />
          <div className="message-preview"><span className="eyebrow">Telefon önizleme</span><div className="sms-bubble">{message}</div></div>
          {lastSend && <div className="sms-send-summary"><CheckCircle2 size={18} /><span><strong>{lastSend.queuedCount} SMS kuyruğa alındı.</strong>{lastSend.skipped.length > 0 ? ` ${lastSend.skipped.length} müşteri izin veya telefon bilgisi nedeniyle atlandı.` : ""}</span></div>}
          <button className="button primary wide" disabled={!sendableCustomers.length || !message.trim() || !integrationReady} onClick={() => { setSendError(""); smsAttemptKey.current = crypto.randomUUID(); setConfirm(true); }}><Send size={17} /> {integrationReady ? `${sendableCustomers.length} kişiye SMS gönder` : "ClickSend bağlantısı gerekli"}</button>
        </section>
        <section className="panel recipients-panel">
          <div className="panel-heading"><div><span className="eyebrow">Alıcı listesi</span><h3>{selected.size} müşteri seçili</h3></div><span className="count-badge">{visibleRecipients.length}</span></div>
          <div className="recipient-actions"><button onClick={() => setSelected(new Set(customers.filter((item) => item.status === "active" && canReceiveSms(item)).map((item) => item.id)))}>Aktifler</button><button onClick={() => setSelected(new Set(customers.filter((item) => item.status === "inactive" && canReceiveSms(item)).map((item) => item.id)))}>Pasifler</button><button onClick={() => setSelected(new Set(customers.filter(canReceiveSms).map((item) => item.id)))}>Tümü</button><button onClick={() => setSelected(new Set())}>Temizle</button></div>
          <label className="inline-search recipient-search"><Search size={16} /><input value={recipientQuery} onChange={(event) => setRecipientQuery(event.target.value)} placeholder="Müşteri ara" /></label>
          <div className="recipient-list">{visibleRecipients.map((customer) => {
            const phoneState = smsPhoneState(customer.phone);
            const disabled = !canReceiveSms(customer);
            const reason = customer.dontSendSms
              ? "SMS izni yok"
              : phoneState === "missing"
                ? "Telefon yok"
                : phoneState === "invalid"
                  ? "Telefon geçersiz"
                  : "";
            return <button key={customer.id} className={selected.has(customer.id) ? "selected" : ""} onClick={() => !disabled && toggle(customer.id)} disabled={disabled}><span className="avatar tiny">{customer.initials}</span><span><strong>{customer.name}</strong><small>{customer.phone || "Telefon girilmemiş"}{reason ? ` · ${reason}` : ""}</small></span><i>{selected.has(customer.id) && <Check size={14} />}</i></button>;
          })}</div>
        </section>
      </div>
      {confirm && <Modal title="SMS gönderimini onayla" eyebrow={`${sendableCustomers.length} gönderilebilir alıcı · yaklaşık ${Math.max(1, Math.ceil(message.length / 153))} SMS/kişi`} onClose={() => { if (!sending) setConfirm(false); }}>
        <div className="sms-preflight-grid">
          <div><span>Seçilen</span><strong>{selectedCustomers.length}</strong></div>
          <div className="ready"><span>Gönderilecek</span><strong>{sendableCustomers.length}</strong></div>
          <div><span>Atlanacak</span><strong>{invalidSelectedCount + missingPhoneCount + permissionBlockedCount}</strong></div>
        </div>
        <div className="confirm-message"><MessageSquareText size={28} /><p>Mesaj yalnızca SMS izni bulunan ve telefonu geçerli {sendableCustomers.length} müşteriye ClickSend üzerinden gönderilecek.</p></div>
        {(invalidSelectedCount + missingPhoneCount + permissionBlockedCount) > 0 && <div className="payment-limit-note">{invalidSelectedCount} geçersiz telefon, {missingPhoneCount} eksik telefon ve {permissionBlockedCount} izinsiz müşteri gönderime dahil edilmeyecek.</div>}
        {sendError && <div className="form-error">{sendError}</div>}
        <div className="modal-actions"><button className="button ghost" disabled={sending} onClick={() => setConfirm(false)}>Geri dön</button><button className="button primary" disabled={sending || !sendableCustomers.length} onClick={() => void submitSms()}>{sending ? "ClickSend’e iletiliyor…" : `${sendableCustomers.length} kişiye gönder`}</button></div>
      </Modal>}
    </>
  );
}

const scheduleDays = [
  { iso: 1, short: "Pzt", label: "Pazartesi" },
  { iso: 2, short: "Sal", label: "Salı" },
  { iso: 3, short: "Çar", label: "Çarşamba" },
  { iso: 4, short: "Per", label: "Perşembe" },
  { iso: 5, short: "Cum", label: "Cuma" },
  { iso: 6, short: "Cmt", label: "Cumartesi" },
  { iso: 7, short: "Paz", label: "Pazar" }
];

function monthKey() {
  return istanbulToday().slice(0, 7);
}

function shiftMonthKey(value: string, amount: number) {
  return shiftIsoMonth(`${value}-01`, amount).slice(0, 7);
}

function monthCalendar(value: string) {
  const [year, month] = value.split("-").map(Number);
  const dayCount = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const leading = (new Date(Date.UTC(year, month - 1, 1)).getUTCDay() + 6) % 7;
  return [
    ...Array.from({ length: leading }, () => null),
    ...Array.from({ length: dayCount }, (_, index) =>
      `${value}-${String(index + 1).padStart(2, "0")}`
    )
  ];
}

function staffInitials(name: string) {
  return name.split(" ").map((part) => part[0]).slice(0, 2).join("").toLocaleUpperCase("tr-TR");
}

function shiftRail(startTime: string, endTime: string) {
  const minutes = (value: string) => {
    const [hour, minute] = value.split(":").map(Number);
    return hour * 60 + minute;
  };
  const dayStart = 8 * 60;
  const dayLength = 13 * 60;
  return {
    marginLeft: `${Math.max(0, ((minutes(startTime) - dayStart) / dayLength) * 100)}%`,
    width: `${Math.min(100, ((minutes(endTime) - minutes(startTime)) / dayLength) * 100)}%`
  };
}

function ScheduleExceptionForm({
  onClose,
  onSaved
}: {
  onClose: () => void;
  onSaved: (count: number) => void;
}) {
  const {
    staff,
    staffSchedules,
    scheduleExceptions,
    createScheduleExceptions
  } = useStudio();
  const trainers = staff.filter((item) => item.isTrainer);
  const [staffId, setStaffId] = useState(trainers[0]?.id ?? "");
  const [calendarMonth, setCalendarMonth] = useState(monthKey());
  const [selectedDates, setSelectedDates] = useState(() => new Set([today()]));
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("17:00");
  const [reason, setReason] = useState("İzin");
  const [detail, setDetail] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const calendarDays = monthCalendar(calendarMonth);
  const selectedStaff = trainers.find((item) => item.id === staffId);
  const selectedList = [...selectedDates].sort();

  useEffect(() => {
    const shift = staffSchedules.find((item) => item.staffId === staffId && item.active);
    if (!shift) return;
    setStartTime(shift.startTime);
    setEndTime(shift.endTime);
  }, [staffId, staffSchedules]);

  function toggleDate(date: string) {
    setSelectedDates((current) => {
      const next = new Set(current);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
    setError("");
  }

  function selectWorkWeek() {
    const current = today();
    const isoDay = new Date(`${current}T12:00:00Z`).getUTCDay() || 7;
    const monday = addIsoDays(current, 1 - isoDay);
    const dates = Array.from({ length: 6 }, (_, index) => {
      return addIsoDays(monday, index);
    });
    setSelectedDates(new Set(dates));
    setCalendarMonth(dates[0].slice(0, 7));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!staffId || selectedList.length === 0) {
      setError("En az bir çalışan ve bir gün seçin.");
      return;
    }
    if (!allDay && endTime <= startTime) {
      setError("Bitiş saati başlangıç saatinden sonra olmalıdır.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createScheduleExceptions({
        staffId,
        dates: selectedList,
        startTime,
        endTime,
        allDay,
        note: detail.trim() ? `${reason} · ${detail.trim()}` : reason
      });
      onSaved(selectedList.length);
    } catch {
      setError("İzin kaydı oluşturulamadı. Seçilen günlerde çakışan bir kayıt olup olmadığını kontrol edin.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="İzin ve saat istisnası" eyebrow="Çalışanı, günleri ve saatleri seç" onClose={onClose} wide>
      <form onSubmit={submit} className="schedule-exception-form">
        <div className="exception-builder">
          <section className="exception-builder-controls">
            <label className="field full">
              <span>Çalışan</span>
              <select value={staffId} onChange={(event) => setStaffId(event.target.value)} required>
                {trainers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>

            <div className="exception-reason">
              <span className="field-label">Neden</span>
              <div className="reason-options">
                {["İzin", "Eğitim", "Kişisel", "Diğer"].map((item) => (
                  <button type="button" key={item} className={reason === item ? "active" : ""} onClick={() => setReason(item)}>
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div className="all-day-choice">
              <button type="button" className={allDay ? "active" : ""} onClick={() => setAllDay(true)}>
                <CalendarDays size={17} />
                <span><strong>Tüm gün</strong><small>Günün tamamında izinli</small></span>
                {allDay && <Check size={16} />}
              </button>
              <button type="button" className={!allDay ? "active" : ""} onClick={() => setAllDay(false)}>
                <Clock3 size={17} />
                <span><strong>Belirli saatler</strong><small>2–3 saatlik izinler için</small></span>
                {!allDay && <Check size={16} />}
              </button>
            </div>

            {!allDay && (
              <div className="form-row exception-time-row">
                <label className="field"><span>Başlangıç</span><select value={startTime} onChange={(event) => setStartTime(event.target.value)} required>{fullHourOptions.slice(0, -1).map((hour) => <option key={hour}>{hour}</option>)}</select></label>
                <label className="field"><span>Bitiş</span><select value={endTime} onChange={(event) => setEndTime(event.target.value)} required>{fullHourOptions.slice(1).map((hour) => <option key={hour}>{hour}</option>)}</select></label>
              </div>
            )}

            <label className="field full">
              <span>Açıklama <i>isteğe bağlı</i></span>
              <input value={detail} onChange={(event) => setDetail(event.target.value)} maxLength={400} placeholder="Örn. doktor randevusu" />
            </label>
          </section>

          <section className="exception-calendar">
            <div className="exception-calendar-head">
              <button type="button" className="icon-button" onClick={() => setCalendarMonth((value) => shiftMonthKey(value, -1))} aria-label="Önceki ay"><ChevronLeft size={18} /></button>
              <strong>{new Intl.DateTimeFormat("tr-TR", { month: "long", year: "numeric" }).format(new Date(`${calendarMonth}-15T12:00:00`))}</strong>
              <button type="button" className="icon-button" onClick={() => setCalendarMonth((value) => shiftMonthKey(value, 1))} aria-label="Sonraki ay"><ChevronRight size={18} /></button>
            </div>
            <div className="calendar-quick-actions">
              <button type="button" onClick={() => { setSelectedDates(new Set([today()])); setCalendarMonth(today().slice(0, 7)); }}>Bugün</button>
              <button type="button" onClick={selectWorkWeek}>Bu çalışma haftası</button>
              <button type="button" onClick={() => setSelectedDates(new Set())}>Seçimi temizle</button>
            </div>
            <div className="exception-calendar-grid weekday-head">
              {scheduleDays.map((day) => <span key={day.iso}>{day.short}</span>)}
            </div>
            <div className="exception-calendar-grid day-grid">
              {calendarDays.map((date, index) =>
                date ? (
                  <button
                    type="button"
                    key={date}
                    className={[
                      selectedDates.has(date) ? "selected" : "",
                      date === today() ? "today" : "",
                      scheduleExceptions.some((item) => item.staffId === staffId && item.date === date) ? "has-exception" : ""
                    ].filter(Boolean).join(" ")}
                    onClick={() => toggleDate(date)}
                    aria-pressed={selectedDates.has(date)}
                  >
                    <span>{Number(date.slice(-2))}</span>
                  </button>
                ) : <i key={`empty-${index}`} />
              )}
            </div>
          </section>
        </div>

        <div className="exception-selection-summary">
          <span className="avatar">{staffInitials(selectedStaff?.name ?? "THH")}</span>
          <span>
            <strong>{selectedList.length ? `${selectedList.length} gün seçildi` : "Henüz gün seçilmedi"}</strong>
            <small>{allDay ? "Tüm gün izin" : `${startTime}–${endTime}`} · {reason}</small>
          </span>
          {!!selectedList.length && <em>{selectedList.slice(0, 3).map((date) => shortDate(date)).join(", ")}{selectedList.length > 3 ? ` +${selectedList.length - 3}` : ""}</em>}
        </div>
        {error && <div className="form-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="button ghost" onClick={onClose}>Vazgeç</button>
          <button className="button primary" disabled={saving || !staffId || !selectedList.length}>
            {saving ? "Programa ekleniyor…" : selectedList.length ? `${selectedList.length} günü programa ekle` : "Günleri programa ekle"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function TeamPage({ onToast }: { onToast: (message: string) => void }) {
  const {
    staff,
    staffSchedules,
    scheduleExceptions,
    cancelScheduleException
  } = useStudio();
  const [tab, setTab] = useState<"schedule" | "exceptions">("schedule");
  const [createOpen, setCreateOpen] = useState(false);
  const [staffFilter, setStaffFilter] = useState("all");
  const trainers = staff.filter((item) => item.isTrainer);
  const upcomingExceptions = scheduleExceptions
    .filter((item) => item.date >= today())
    .filter((item) => staffFilter === "all" || item.staffId === staffFilter)
    .sort((left, right) => `${left.date}${left.startTime}`.localeCompare(`${right.date}${right.startTime}`));

  async function removeException(exception: TrainerException) {
    if (!window.confirm(`${exception.trainer} için ${shortDate(exception.date)} tarihli izin kaydı kaldırılsın mı?`)) return;
    try {
      await cancelScheduleException(exception);
      onToast("İzin kaydı programdan kaldırıldı.");
    } catch {
      onToast("İzin kaydı kaldırılamadı. Lütfen yeniden deneyin.");
    }
  }

  return (
    <>
      <Heading
        eyebrow="Çalışma saatleri ve izin planı"
        title="Ekip programı"
        description="Standart vardiyaları görün; tam günlük veya saatlik izinleri birkaç günü birlikte seçerek programa ekleyin."
        action={<button className="button primary" onClick={() => setCreateOpen(true)}><Plus size={18} /> İzin ekle</button>}
      />
      <div className="module-tabs">
        <button className={tab === "schedule" ? "active" : ""} onClick={() => setTab("schedule")}>Standart çalışma</button>
        <button className={tab === "exceptions" ? "active" : ""} onClick={() => setTab("exceptions")}>İzin & istisnalar <i>{scheduleExceptions.filter((item) => item.date >= today()).length}</i></button>
      </div>

      {tab === "schedule" ? (
        <>
          <div className="schedule-day-scale"><span>08:00</span><i /><span>14:00</span><i /><span>21:00</span></div>
          <div className="team-grid schedule-team-grid">
            {trainers.map((trainer) => {
              const trainerSchedule = staffSchedules.filter((item) => item.staffId === trainer.id && item.active);
              const mainShift = trainerSchedule[0];
              const upcomingLeaveCount = scheduleExceptions.filter((item) => item.staffId === trainer.id && item.date >= today()).length;
              return (
                <section className="panel trainer-card schedule-trainer-card" key={trainer.id}>
                  <div className="trainer-head">
                    <span className="avatar large">{staffInitials(trainer.name)}</span>
                    <div>
                      <h3>{trainer.name}</h3>
                      <span>{trainerSchedule.length} çalışma günü · {upcomingLeaveCount} yaklaşan izin</span>
                    </div>
                    {mainShift && <strong className="shift-badge">{mainShift.startTime}–{mainShift.endTime}</strong>}
                  </div>
                  <div className="trainer-shift-rail" aria-hidden="true">
                    {mainShift && <span style={shiftRail(mainShift.startTime, mainShift.endTime)} />}
                  </div>
                  <div className="schedule-list schedule-readonly-list">
                    {scheduleDays.map((day) => {
                      const entry = trainerSchedule.find((item) => (item.dayOfWeekIso ?? day.iso) === day.iso && item.day === day.label);
                      return (
                        <div key={day.iso} className={entry ? "active" : "off"}>
                          <span>{day.label}</span>
                          {entry
                            ? <strong>{entry.startTime}<i>–</i>{entry.endTime}</strong>
                            : <em>Çalışmıyor</em>}
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      ) : (
        <section className="exception-workspace">
          <div className="exception-toolbar">
            <div>
              <span className="eyebrow">Yaklaşan kayıtlar</span>
              <h3>{upcomingExceptions.length} izin veya saat istisnası</h3>
            </div>
            <div className="staff-filter exception-staff-filter">
              <button className={staffFilter === "all" ? "active" : ""} onClick={() => setStaffFilter("all")}>Tüm ekip</button>
              {trainers.map((item) => <button key={item.id} className={staffFilter === item.id ? "active" : ""} onClick={() => setStaffFilter(item.id)}>{item.name.split(" ")[0]}</button>)}
            </div>
          </div>
          {upcomingExceptions.length ? (
            <div className="data-panel">
              <div className="table-head exception-grid"><span>Çalışan</span><span>Gün</span><span>İzin süresi</span><span>Açıklama</span><span /></div>
              {upcomingExceptions.map((item) => (
                <div className="table-row exception-grid" key={item.id}>
                  <span className="person-cell"><i className="avatar tiny">{staffInitials(item.trainer)}</i><strong>{item.trainer}</strong></span>
                  <span><strong>{shortDate(item.date)}</strong><small>{new Intl.DateTimeFormat("tr-TR", { weekday: "long" }).format(new Date(`${item.date}T12:00:00`))}</small></span>
                  <span className={item.allDay ? "all-day-label" : ""}>{item.allDay ? "Tüm gün" : `${item.startTime}–${item.endTime}`}</span>
                  <strong>{item.note}</strong>
                  <button className="icon-button" onClick={() => removeException(item)} aria-label="İzin kaydını kaldır"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state schedule-empty"><CalendarDays size={28} /><strong>Yaklaşan izin kaydı yok</strong><span>Yeni bir izin veya saat istisnası ekleyebilirsiniz.</span><button className="button secondary" onClick={() => setCreateOpen(true)}><Plus size={16} /> İzin ekle</button></div>
          )}
        </section>
      )}

      {createOpen && (
        <ScheduleExceptionForm
          onClose={() => setCreateOpen(false)}
          onSaved={(count) => {
            setCreateOpen(false);
            setTab("exceptions");
            onToast(`${count} gün için izin programa eklendi.`);
          }}
        />
      )}
    </>
  );
}
